import ChatChannel from '../channels/chat_channel'
import NotificationChannel, { playNotificationAudio } from '../channels/notification_channel'
import ChatAPI from './chat_api'
import { escapeHtml, formatTimeAgo, formatMessageTime, formatFileDate, formatFullDateTime } from '../chat/utils'
import { ChatState } from '../chat/state'
import { buildUndoButton, clearUndoButtons, removeUndoneMessage, initChatUndo, onMessageUndone } from '../chat/undo'

const CHAT_MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024
const CHAT_ALLOWED_IMAGE_VIDEO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/x-msvideo']
const CHAT_ALLOWED_DOC_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
const IMAGE_VIDEO_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png', 'webp', 'mp4', 'mov', 'avi']
const DOC_EXTENSIONS = ['pdf', 'doc', 'docx']

// Cross-tab notification sync — storage event fires on OTHER tabs when localStorage changes
window.addEventListener('storage', (e) => {
  if (e.key === 'chat_notification_sync' && e.newValue) {
    try {
      const data = JSON.parse(e.newValue);

      // Update all badges
      if (data.unread_count != null) {
        const badges = document.querySelectorAll('.fab-badge[data-chat-notification-badge], .notification-badge, [data-notification-badge], [data-chat-notification-badge]');
        badges.forEach((badge) => {
          const count = data.unread_count;
          badge.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
          badge.style.display = count > 0 ? 'flex' : 'none';
          badge.classList.toggle('has-notifications', count > 0);
        });
      }

      // Update tab unread badges
      if (window.updateTabUnreadBadges && data.first_tab_unread != null) {
        window.updateTabUnreadBadges(data.first_tab_unread || 0, data.second_tab_unread || 0);
      }

      // Play notification sound
      if (data.play_sound) {
        try { playNotificationAudio(); } catch (err) {}
      }
    } catch (err) {}
  }
})

let currentChatSubscription = null
let notificationSubscription = null

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function hideBootstrapModal(modal) {
  if (!modal) return
  if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
    const bsModal = bootstrap.Modal.getInstance(modal)
    if (bsModal) bsModal.dispose()
  }
  // Remove the modal from the DOM entirely to prevent stale state and event listener leaks
  modal.remove()
  document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
  document.body.classList.remove('modal-open')
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
}

function applyFullscreenModalStyles(modal, maxWidth = '600px') {
  if (!modal) return
  Object.assign(modal.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    width: '100vw',
    height: '100vh',
    zIndex: '10000',
    margin: '0',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  })
  const dialog = modal.querySelector('.modal-dialog')
  if (dialog) {
    dialog.style.margin = '0'
    dialog.style.maxWidth = maxWidth
  }
}

function resetModalStyles(modal) {
  if (!modal) return
  const props = ['position', 'top', 'left', 'right', 'bottom', 'width', 'height', 'zIndex']
  props.forEach(prop => modal.style[prop] = '')
}

function fetchScript(url, onSuccess) {
  fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'text/javascript',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': getCsrfToken()
    },
    credentials: 'same-origin'
  })
  .then(response => {
    if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`)
    return response.text()
  })
  .then(text => {
    try { eval(text) } catch (e) { console.error('Error evaluating script:', e) }
    if (onSuccess) onSuccess()
  })
  .catch(error => console.error('Error loading script:', error))
}

function chatValidateFile(file, kind) {
  if (!file || !file.name) return { valid: false, error: 'Invalid file' }
  if (file.size > CHAT_MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: `"${file.name}" exceeds 200MB. Please choose a smaller file.` }
  }
  const ext = (file.name.split('.').pop() || '').toLowerCase()
  const type = (file.type || '').toLowerCase()
  
  if (kind === 'document') {
    const ok = CHAT_ALLOWED_DOC_TYPES.includes(type) || DOC_EXTENSIONS.includes(ext)
    if (!ok) return { valid: false, error: `"${file.name}" is not allowed. Use PDF, DOC, or DOCX.` }
  } else {
    const ok = CHAT_ALLOWED_IMAGE_VIDEO_TYPES.includes(type) || IMAGE_VIDEO_EXTENSIONS.includes(ext)
    if (!ok) return { valid: false, error: `"${file.name}" is not allowed. Use images or videos.` }
  }
  return { valid: true }
}

function chatAttachmentType(file) {
  const t = (file.type || '').toLowerCase()
  if (t.startsWith('image/')) return 'image'
  if (t.startsWith('video/')) return 'video'
  return 'document'
}

function chatBuildAttachmentsFromFiles(files, attachmentType) {
  return Array.from(files || []).map(file => ({
    file,
    file_name: file.name,
    file_type: file.type || '',
    file_size: file.size,
    attachment_type: attachmentType || chatAttachmentType(file)
  }))
}

// Clipboard images arrive unnamed (or as "image.png"); give them a unique name.
function chatNamePastedImage(file) {
  const ext = ((file.type || 'image/png').split('/')[1] || 'png').replace('jpeg', 'jpg');
  try {
    return new File([file], `screenshot-${Date.now()}.${ext}`, { type: file.type });
  } catch (e) {
    return file;
  }
}

function setChatSendingState(show, percent = 0) {
  const el = document.getElementById('conversation-sending-indicator')
  if (!el) return
  const textEl = el.querySelector('.sending-text')
  const fillEl = el.querySelector('.sending-progress-fill')
  if (show) {
    el.classList.remove('hidden')
    if (textEl) textEl.textContent = percent > 0 ? `Sending... ${percent}%` : 'Sending...'
    if (fillEl) fillEl.style.width = `${percent}%`
    const sendBtn = document.getElementById('conversation-send-btn')
    const attachBtn = document.querySelector('[data-action="show-file-upload"]')
    if (sendBtn) sendBtn.disabled = true
    if (attachBtn) attachBtn.setAttribute('aria-disabled', 'true')
  } else {
    el.classList.add('hidden')
    if (fillEl) fillEl.style.width = '0%'
    const sendBtn = document.getElementById('conversation-send-btn')
    const attachBtn = document.querySelector('[data-action="show-file-upload"]')
    if (sendBtn) sendBtn.disabled = false
    if (attachBtn) attachBtn.removeAttribute('aria-disabled')
  }
}

function cleanupModalOnHide(modal) {
  document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
  document.body.classList.remove('modal-open')
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  // Remove the modal from the DOM entirely so it's recreated fresh on next open
  if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
    const inst = bootstrap.Modal.getInstance(modal)
    if (inst) inst.dispose()
  }
  modal.remove()
}

function showBootstrapModal(modal, maxWidth = '600px') {
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal)
  }
  applyFullscreenModalStyles(modal, maxWidth)

  if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
    const existingModal = bootstrap.Modal.getInstance(modal)
    if (existingModal) existingModal.dispose()

    // Remove .fade to prevent Bootstrap transition delay — show instantly
    modal.classList.remove('fade')
    const bsModal = new bootstrap.Modal(modal, { backdrop: true, keyboard: true, focus: true })
    bsModal.show()

    modal.addEventListener('hidden.bs.modal', () => cleanupModalOnHide(modal), { once: true })
    modal.addEventListener('click', (e) => { if (e.target === modal) bsModal.hide() })
    return bsModal
  } else {
    modal.classList.add('show')
    modal.style.display = 'flex'
    modal.setAttribute('aria-hidden', 'false')
    modal.setAttribute('aria-modal', 'true')
    document.body.classList.add('modal-open')
    return null
  }
}

let chatListenersRegistered = false;

document.addEventListener('turbolinks:load', () => {

  if (chatListenersRegistered) {
    // Listeners already registered — skip to per-page-load init
    chatPerPageInit();
    return;
  }
  chatListenersRegistered = true;

  initChatUndo();
  // Refresh the list preview / unread badges after this user undoes a message.
  onMessageUndone(() => loadConversations(true));

  // Chat Settings Button
  document.addEventListener('click', (e) => {
    const settingsBtn = e.target.closest('[data-action="open-chat-settings"]')
    if (settingsBtn) {
      const modal = document.getElementById('chatSettingsModal')
      if (modal) {
        loadChatSettingsIntoModal(modal)
        showBootstrapModal(modal, '800px')
      }
    }
  })

  async function loadChatSettingsIntoModal(modal) {
    if (!modal) return;
    try {
      const settings = await ChatAPI.getChatSettings();
      ChatState.notificationSettings = {
        allow_desktop_notifications: settings.allow_desktop_notifications !== false,
        notification_sound: settings.notification_sound !== false,
        email_notifications: settings.email_notifications !== false,
        notification_volume: settings.notification_volume ?? 0.5
      };
      const allowEl = modal.querySelector('#allow-notifications');
      const soundEl = modal.querySelector('#notification-sound');
      const emailEl = modal.querySelector('#email-notification');
      const volumeEl = modal.querySelector('#notification-volume');
      const volumeDisplay = modal.querySelector('#volume-display');
      if (allowEl) allowEl.checked = ChatState.notificationSettings.allow_desktop_notifications;
      if (soundEl) soundEl.checked = ChatState.notificationSettings.notification_sound;
      if (emailEl) emailEl.checked = ChatState.notificationSettings.email_notifications;
      if (volumeEl) {
        volumeEl.value = ChatState.notificationSettings.notification_volume;
        if (volumeDisplay) volumeDisplay.textContent = Math.round(ChatState.notificationSettings.notification_volume * 100) + '%';
        volumeEl.addEventListener('input', () => {
          if (volumeDisplay) volumeDisplay.textContent = Math.round(volumeEl.value * 100) + '%';
        });
      }
    } catch (e) {
      console.error('Error loading chat settings:', e);
    }
  }

  document.addEventListener('click', async (e) => {
    const saveBtn = e.target.closest('[data-action="save-chat-settings"]');
    if (saveBtn) {
      const modal = document.getElementById('chatSettingsModal');
      if (!modal) return;
      const allowEl = modal.querySelector('#allow-notifications');
      const soundEl = modal.querySelector('#notification-sound');
      const emailEl = modal.querySelector('#email-notification');
      const volumeEl = modal.querySelector('#notification-volume');
      const settings = {
        allow_desktop_notifications: allowEl?.checked !== false,
        notification_sound: soundEl?.checked !== false,
        email_notifications: emailEl?.checked !== false,
        notification_volume: volumeEl ? parseFloat(volumeEl.value) : 0.5
      };
      saveBtn.disabled = true;
      try {
        const updated = await ChatAPI.updateChatSettings(settings);
        ChatState.notificationSettings = {
          allow_desktop_notifications: updated.allow_desktop_notifications !== false,
          notification_sound: updated.notification_sound !== false,
          email_notifications: updated.email_notifications !== false,
          notification_volume: updated.notification_volume ?? 0.5
        };
        if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const bsModal = bootstrap.Modal.getInstance(modal);
          if (bsModal) bsModal.hide();
        }
      } catch (err) {
        console.error('Error saving chat settings:', err);
        alert(err.message || 'Failed to save settings. Please try again.');
      } finally {
        saveBtn.disabled = false;
      }
    }
  });

  function getNewChatTabContext() {
    // Always verify against DOM to prevent stale state
    const domTab = getActiveChatTab();
    if (ChatState.currentTab !== domTab) {
      ChatState.currentTab = domTab;
    }
    const tab = ChatState.currentTab;
    const btn = document.querySelector('.btn-new-chat');
    let tabName;

    if (btn) {
      tabName = tab === 'second'
        ? (btn.getAttribute('data-second-tab-name') || 'Aoka')
        : (btn.getAttribute('data-first-tab-name') || 'City');
    } else {
      tabName = 'City';
    }
    
    // Determine category based on actual tab name (role-aware)
    const tabNameLower = tabName.toLowerCase();
    let category;
    if (tabNameLower === 'contractors' || tabNameLower === 'contractor' || tabNameLower === 'applicants') {
      category = 'contractors';
    } else if (tabNameLower === 'aoka') {
      category = 'aoka';
    } else if (tabNameLower === 'city') {
      category = 'city';
    } else {
      category = tab === 'second' ? 'aoka' : 'city';
    }
    
    return { tab, category, tabName };
  }

  function updateNewChatButtonLabel() {
    const btn = document.querySelector('.btn-new-chat');
    if (!btn) return;
    const labelSpan = btn.querySelector('.btn-label');
    const { tabName, category } = getNewChatTabContext();
    if (labelSpan) labelSpan.textContent = 'New Chat with ' + tabName;
    btn.classList.toggle('btn-new-chat--aoka', category === 'aoka');
  }

  document.addEventListener('click', async (e) => {
    const newChatBtn = e.target.closest('[data-action="new-chat"]');
    if (newChatBtn) {
      await openNewChatModal();
    }
  });

  async function openNewChatModal() {
    const { category, tabName } = getNewChatTabContext();
    // Map category to tab parameter for API calls
    let tab;
    if (category === 'contractors') {
      tab = 'contractors';
    } else if (category === 'aoka') {
      tab = 'aoka';
    } else {
      tab = 'city';
    }

    // Show modal instantly with cached or empty data, then backfill
    const cached = ChatState.cachedConversations || [];
    renderNewChatModal(cached, [], [], category, tabName);

    try {
      const [groups, individuals] = await Promise.all([
        ChatAPI.fetchGroups('', tab),
        ChatAPI.fetchIndividuals('', tab)
      ]);
      populateGroupsList(groups);
      populateIndividualsList(individuals);
    } catch (error) {
      // Modal already visible with whatever cached data we had
    }
  }

  function renderNewChatModal(conversations, groups, individuals, category, tabName) {
    const ctx = category != null ? { category, tabName: tabName || (category === 'aoka' ? 'Aoka' : 'City') } : getNewChatTabContext();
    const modalCategory = ctx.category || 'city';
    const modalTabName = ctx.tabName || 'City';

    let modal = document.getElementById('newChatModal');
    
    if (!modal) {
      const modalHTML = `
        <div id="newChatModal" class="modal fade" tabindex="-1" aria-labelledby="newChatModalLabel" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content new-chat-modal-content">
              <div class="new-chat-modal-header">
                <div class="modal-header-content">
                  <h5 class="modal-title" id="newChatModalLabel">Start New Chat with ${escapeHtml(modalTabName)}</h5>
                  <p class="modal-subtitle">Start a direct chat or create a group.</p>
                </div>
                <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close">×</button>
              </div>
              <div class="modal-body new-chat-modal-body">
                <div class="new-chat-search">
                  <div class="search-input-wrapper">
                    <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input class="form-control search-input" type="text" placeholder="Search..." id="new-chat-search-input">
                  </div>
                </div>
                <div class="new-chat-filters">
                  <button class="new-chat-filter active" data-filter="individuals">
                    <span>Individual</span>
                  </button>
                  <button class="new-chat-filter" data-filter="groups">
                    <span>Group</span>
                  </button>
                </div>
                <div class="create-new-group-section" style="display: none;">
                  <button class="btn-create-new-group" type="button" data-action="create-new-group">
                    <svg class="plus-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    <span>Create New Group</span>
                  </button>
                </div>
                <div class="new-chat-list-container">
                  <div id="new-chat-individuals" class="new-chat-list active"></div>
                  <div id="new-chat-groups" class="new-chat-list"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = modalHTML;
      modal = tempDiv.querySelector('#newChatModal');
      document.body.appendChild(modal);
    }

    modal.setAttribute('data-category', modalCategory);
    const titleEl = modal.querySelector('#newChatModalLabel');
    if (titleEl) titleEl.textContent = 'Start New Chat with ' + modalTabName;

    const searchInput = modal.querySelector('#new-chat-search-input');
    if (searchInput) searchInput.value = '';

    populateGroupsList(groups);
    populateIndividualsList(individuals);

    showNewChatModal(modal);
  }

  let newChatSearchDebounceTimer = null;
  async function applyNewChatSearch() {
    const modal = document.getElementById('newChatModal');
    if (!modal) return;
    const tab = modal.getAttribute('data-category') || 'city';
    const input = modal.querySelector('#new-chat-search-input');
    const query = input ? input.value.trim() : '';
    const activeBtn = modal.querySelector('.new-chat-filter.active');
    const filterType = (activeBtn && activeBtn.getAttribute('data-filter')) || 'individuals';
    const container = document.getElementById(`new-chat-${filterType}`);
    if (!container) return;

    if (query) {
      container.innerHTML = '<div class="empty-new-chat"><p>Searching…</p></div>';
    }

    try {
      let data = [];
      if (filterType === 'groups') {
        data = await ChatAPI.fetchGroups(query, tab);
        populateGroupsList(data);
      } else if (filterType === 'individuals') {
        data = await ChatAPI.fetchIndividuals(query, tab);
        populateIndividualsList(data);
      }
    } catch (err) {
      console.error('New chat search failed:', err);
      container.innerHTML = '<div class="empty-new-chat"><p>Search failed. Try again.</p></div>';
    }
  }

  function scheduleNewChatSearch() {
    if (newChatSearchDebounceTimer) clearTimeout(newChatSearchDebounceTimer);
    newChatSearchDebounceTimer = setTimeout(() => {
      newChatSearchDebounceTimer = null;
      applyNewChatSearch();
    }, 300);
  }


  function populateGroupsList(groups) {
    const container = document.getElementById('new-chat-groups');
    if (!container) return;

    if (groups.length === 0) {
      const searchInput = document.getElementById('new-chat-search-input');
      const hasSearch = searchInput && searchInput.value.trim();
      container.innerHTML = '<div class="empty-new-chat"><svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg><p>' + (hasSearch ? 'No matching groups' : 'No groups available') + '</p></div>';
      return;
    }

    container.innerHTML = groups.map(group => `
      <div class="new-chat-item" data-group-id="${group.id}" data-group-name="${escapeHtml(group.name)}">
        <div class="city-avatar">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div class="city-info">
          <div class="city-name">${escapeHtml(group.name)}</div>
          <div class="city-meta">
            <span>${escapeHtml(group.city_name || 'Unknown')}</span>
            <span class="meta-separator"> • </span>
            <span>${group.member_count || 0} Members</span>
          </div>
        </div>
      </div>
    `).join('');
  }

  function populateIndividualsList(individuals) {
    const container = document.getElementById('new-chat-individuals');
    if (!container) return;

    if (individuals.length === 0) {
      const searchInput = document.getElementById('new-chat-search-input');
      const hasSearch = searchInput && searchInput.value.trim();
      container.innerHTML = '<div class="empty-new-chat"><svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg><p>' + (hasSearch ? 'No matching individuals' : 'No individuals available') + '</p></div>';
      return;
    }

    container.innerHTML = individuals.map(individual => `
      <div class="new-chat-item" data-individual-id="${individual.id}" data-individual-name="${escapeHtml(individual.name)}" data-individual-city="${escapeHtml(individual.city || '')}">
        <div class="city-avatar">
          <span class="avatar-initials">${escapeHtml(individual.initials || 'U')}</span>
        </div>
        <div class="city-info">
          <div class="city-name">${escapeHtml(individual.name)}</div>
          <div class="city-meta">${escapeHtml(individual.city || individual.role || '')}</div>
        </div>
      </div>
    `).join('');
  }

  function showNewChatModal(modal) {
    showBootstrapModal(modal, '600px')
  }

  document.addEventListener('input', (e) => {
    if (e.target.id === 'new-chat-search-input') {
      scheduleNewChatSearch();
    }
  });

  document.addEventListener('click', (e) => {
    const filterBtn = e.target.closest('.new-chat-filter');
    if (filterBtn) {
      const filterType = filterBtn.getAttribute('data-filter');
      const modal = filterBtn.closest('#newChatModal');
      if (modal && filterType) {
        modal.querySelectorAll('.new-chat-filter').forEach(btn => btn.classList.remove('active'));
        filterBtn.classList.add('active');
        
        modal.querySelectorAll('.new-chat-list').forEach(list => list.classList.remove('active'));
        const targetList = document.getElementById(`new-chat-${filterType}`);
        if (targetList) {
          targetList.classList.add('active');
        }
        
        const searchInput = modal.querySelector('#new-chat-search-input');
        if (searchInput && searchInput.value.trim()) {
          applyNewChatSearch();
        }
        
        const createGroupSection = modal.querySelector('.create-new-group-section');
        if (createGroupSection) {
          if (filterType === 'groups') {
            createGroupSection.style.display = 'block';
          } else {
            createGroupSection.style.display = 'none';
          }
        }
      }
    }

    const conversationItem = e.target.closest('.new-chat-item[data-conversation-id]');
    if (conversationItem) {
      const conversationId = conversationItem.getAttribute('data-conversation-id');
      if (conversationId) {
        const modal = document.getElementById('newChatModal');
        if (modal && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const bsModal = bootstrap.Modal.getInstance(modal);
          if (bsModal) bsModal.hide();
        }
        loadConversationDetail(parseInt(conversationId, 10)).then(() => {
          setActiveConversationInList(conversationId);
        }).catch((err) => console.error('Error opening conversation:', err));
      }
      return;
    }

    const cityItem = e.target.closest('.new-chat-item[data-city-id]');
    if (cityItem) {
      const cityId = cityItem.getAttribute('data-city-id');
      const cityName = cityItem.getAttribute('data-city-name');
      handleCityClick(cityId, cityName);
    }

    const individualItem = e.target.closest('.new-chat-item[data-individual-id]');
    if (individualItem) {
      const userId = individualItem.getAttribute('data-individual-id');
      handleIndividualClick(userId);
    }

    const groupItem = e.target.closest('.new-chat-item[data-group-id]');
    if (groupItem) {
      const groupId = groupItem.getAttribute('data-group-id');
      handleGroupClick(groupId);
    }
  });

  async function handleCityClick(cityId, cityName) {
    hideBootstrapModal(document.getElementById('newChatModal'))
    const createGroupBtn = document.querySelector('[data-action="create-new-group"]')
    if (createGroupBtn) createGroupBtn.click()
  }

  async function handleIndividualClick(userId) {
    try {
      const result = await ChatAPI.createDirectConversation(userId)
      if (result && result.error) {
        alert(result.error)
        return
      }
      if (result && result.conversation) {
        hideBootstrapModal(document.getElementById('newChatModal'))
        await loadConversationDetail(result.conversation.id)
        await loadConversations()
        setActiveConversationInList(result.conversation.id)
      } else {
        alert('Failed to create conversation. Please try again.');
      }
    } catch (error) {
      console.error('Error creating direct conversation:', error);
      alert('Failed to create conversation. Please try again.');
    }
  }

  document.addEventListener('click', async (e) => {
    const startChatBtn = e.target.closest('[data-action="start-direct-chat"]');
    if (startChatBtn) {
      const userId = startChatBtn.getAttribute('data-user-id');
      if (userId) {
        await handleIndividualClick(userId);
        const infoPanel = document.getElementById('chat-info-panel');
        if (infoPanel) {
          infoPanel.classList.add('hidden');
        }
      }
    }
  });

  async function handleGroupClick(groupId) {
    try {
      const id = parseInt(groupId)
      await loadConversationDetail(id)
      setActiveConversationInList(id)
      hideBootstrapModal(document.getElementById('newChatModal'))
    } catch (error) {
      console.error('Error loading group conversation:', error)
      alert('Failed to load conversation. Please try again.')
    }
  }

  document.addEventListener('click', (e) => {
    const createGroupBtn = e.target.closest('[data-action="create-new-group"]')
    if (createGroupBtn) {
      const currentModal = document.getElementById('newChatModal')
      let category = 'city'
      if (currentModal) {
        // Always use fresh tab context to avoid stale data-category from a previous modal open
        category = getNewChatTabContext().category || currentModal.getAttribute('data-category') || 'city'
        ChatState.pendingNewGroupCategory = category
        hideBootstrapModal(currentModal)
      }
      fetchScript(`/chats/select_city_modal?category=${encodeURIComponent(category)}`)
    }
  })

  document.addEventListener('click', (e) => {
    const cityItem = e.target.closest('.city-item')
    if (cityItem) {
      const cityId = cityItem.getAttribute('data-city-id')
      const cityName = cityItem.getAttribute('data-city-name')

      if (cityId && cityName) {
        hideBootstrapModal(document.getElementById('selectCityModal'))
        const url = `/chats/create_new_group_modal?city_id=${encodeURIComponent(cityId)}&city_name=${encodeURIComponent(cityName)}`
        fetchScript(url, () => {
          const m = document.getElementById('createNewGroupModal')
          if (m) m.setAttribute('data-category', ChatState.pendingNewGroupCategory)
        })
      }
    }
  })

  document.addEventListener('click', (e) => {
    const backBtn = e.target.closest('[data-action="close-select-city"]')
    if (backBtn) {
      e.preventDefault()
      e.stopPropagation()
      hideBootstrapModal(backBtn.closest('#selectCityModal') || backBtn.closest('#createNewGroupModal'))
      fetchScript('/chats/new_chat_modal', () => {
        const groupsTab = document.querySelector('#newChatModal [data-filter="groups"]')
        if (groupsTab) groupsTab.click()
      })
    }
  })

  document.addEventListener('click', (e) => {
    const backBtn = e.target.closest('[data-action="back-to-select-city"]')
    if (backBtn) {
      const createGroupModal = backBtn.closest('#createNewGroupModal')
      const category = createGroupModal?.getAttribute('data-category') || ChatState.pendingNewGroupCategory || 'city'
      hideBootstrapModal(createGroupModal)
      fetchScript(`/chats/select_city_modal?category=${encodeURIComponent(category)}`)
    }
  })

  document.addEventListener('input', (e) => {
    if (e.target.id === 'select-city-search-input') {
      const searchTerm = e.target.value.toLowerCase();
      const modal = e.target.closest('#selectCityModal');
      if (modal) {
        const cityItems = modal.querySelectorAll('.city-item');
        cityItems.forEach(item => {
          const name = item.querySelector('.city-name')?.textContent.toLowerCase() || '';
          const meta = item.querySelector('.city-meta')?.textContent.toLowerCase() || '';
          if (name.includes(searchTerm) || meta.includes(searchTerm)) {
            item.style.display = 'flex';
          } else {
            item.style.display = 'none';
          }
        });
      }
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target.id === 'participants-search-input') {
      const searchTerm = e.target.value.toLowerCase();
      const modal = e.target.closest('#createNewGroupModal');
      if (modal) {
        const participantItems = modal.querySelectorAll('.participant-item');
        participantItems.forEach(item => {
          const name = item.querySelector('.participant-name')?.textContent.toLowerCase() || '';
          const role = item.querySelector('.participant-role')?.textContent.toLowerCase() || '';
          const city = item.querySelector('.participant-city')?.textContent.toLowerCase() || '';
          if (name.includes(searchTerm) || role.includes(searchTerm) || city.includes(searchTerm)) {
            item.style.display = 'block';
          } else {
            item.style.display = 'none';
          }
        });
      }
    }
  });

  // Unified group creation handler
  let isCreatingGroup = false

  async function handleCreateGroup(modal, config) {
    if (isCreatingGroup) return false

    const { nameInputId, category, organizationId, participantLabel } = config
    const groupNameInput = modal.querySelector(`#${nameInputId}`)
    const groupName = groupNameInput ? groupNameInput.value.trim() : ''
    const checkedParticipants = Array.from(modal.querySelectorAll('.participant-checkbox:checked')).map(cb => parseInt(cb.value))

    if (!groupName) {
      alert('Please enter a group name')
      return false
    }
    if (checkedParticipants.length === 0) {
      alert(`Please select at least one ${participantLabel}`)
      return false
    }

    isCreatingGroup = true
    const createBtn = modal.querySelector('.btn-create-group')
    if (createBtn) createBtn.disabled = true

    try {
      const result = await ChatAPI.createGroupConversation(groupName, category, organizationId, checkedParticipants)
      if (result.conversation) {
        // Close all chat modals completely before loading conversation detail
        const allChatModals = ['newChatModal', 'selectCityModal', 'createNewGroupModal',
          'createContractorsGroupModal', 'createAokaGroupModal']
        allChatModals.forEach(id => {
          const m = document.getElementById(id)
          if (!m) return
          if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
            const inst = bootstrap.Modal.getInstance(m)
            if (inst) inst.dispose()
          }
          m.remove()
        })
        document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
        document.body.classList.remove('modal-open')
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''

        await loadConversationDetail(result.conversation.id)
        ChatState.clearCache()
        await loadConversations(true)
        return true
      }
    } catch (error) {
      console.error(`Error creating ${category} group:`, error)
      alert(error.message || 'Failed to create group. Please try again.')
    } finally {
      isCreatingGroup = false
      if (createBtn) createBtn.disabled = false
    }
    return false
  }

  document.addEventListener('click', async (e) => {
    const createGroupBtn = e.target.closest('.btn-create-group')
    if (!createGroupBtn) return

    const contractorsModal = createGroupBtn.closest('#createContractorsGroupModal')
    if (contractorsModal) {
      const participantLabel = contractorsModal.getAttribute('data-participant-label') || 'contractor'
      await handleCreateGroup(contractorsModal, { nameInputId: 'contractors-group-name-input', category: 'contractors', organizationId: null, participantLabel })
      return
    }

    const aokaModal = createGroupBtn.closest('#createAokaGroupModal')
    if (aokaModal) {
      await handleCreateGroup(aokaModal, { nameInputId: 'aoka-group-name-input', category: 'aoka', organizationId: null, participantLabel: 'Aoka team member' })
      return
    }

    const modal = createGroupBtn.closest('#createNewGroupModal')
    if (modal) {
      const category = modal.getAttribute('data-category') || 'city'
      const organizationId = modal.getAttribute('data-organization-id')
      await handleCreateGroup(modal, { nameInputId: 'group-name-input', category, organizationId, participantLabel: 'participant' })
    }
  })

  // Handle back button in contractors/aoka group modal
  document.addEventListener('click', (e) => {
    const backBtn = e.target.closest('[data-action="back-to-new-chat"]')
    if (backBtn) {
      hideBootstrapModal(backBtn.closest('#createContractorsGroupModal'))
      hideBootstrapModal(backBtn.closest('#createAokaGroupModal'))
      openNewChatModal()
    }
  })

  // Unified search handler for group modals
  function filterParticipantItems(modal, searchTerm, selector, attrs) {
    if (!modal) return
    const items = modal.querySelectorAll(selector)
    items.forEach(item => {
      const matches = attrs.some(attr => (item.getAttribute(attr) || '').toLowerCase().includes(searchTerm))
      item.style.display = matches ? 'flex' : 'none'
    })
  }

  document.addEventListener('input', (e) => {
    const searchTerm = e.target.value.toLowerCase()
    if (e.target.id === 'contractors-search-input') {
      filterParticipantItems(e.target.closest('#createContractorsGroupModal'), searchTerm, '.participant-select-item', ['data-name', 'data-business'])
    } else if (e.target.id === 'aoka-search-input') {
      filterParticipantItems(e.target.closest('#createAokaGroupModal'), searchTerm, '.participant-select-item', ['data-name', 'data-role'])
    }
  })


  document.addEventListener('click', (e) => {
    const conversationItem = e.target.closest('.conversation-item')
    if (conversationItem) {
      document.querySelectorAll('.conversation-item').forEach(item => item.classList.remove('active'))
      conversationItem.classList.add('active')

      const conversationId = conversationItem.getAttribute('data-conversation-id')
      if (conversationId) {
        loadConversationDetail(parseInt(conversationId))
      }
    }
  })

  document.addEventListener('click', async (e) => {
    const infoBtn = e.target.closest('[data-action="show-info"]');
    if (infoBtn) {
      const conversationId = infoBtn.getAttribute('data-conversation-id');
      if (conversationId) {
        await loadChatInfo(parseInt(conversationId));
      }
    }

    const deleteBtn = e.target.closest('[data-action="delete-conversation"]');
    if (deleteBtn) {
      const conversationId = deleteBtn.getAttribute('data-conversation-id');
      const conversationName = deleteBtn.getAttribute('data-conversation-name');
      if (conversationId) {
        showDeleteChatModal(conversationId, conversationName);
      }
    }

    const leaveBtn = e.target.closest('[data-action="leave-conversation"]');
    if (leaveBtn) {
      const conversationId = leaveBtn.getAttribute('data-conversation-id');
      const conversationName = leaveBtn.getAttribute('data-conversation-name');
      if (conversationId) {
        showLeaveGroupModal(conversationId, conversationName);
      }
    }
  });

  async function loadChatInfo(conversationId) {
    try {
      const data = await ChatAPI.getConversationDetails(conversationId);
      renderChatInfoPanel(data);
    } catch (error) {
      console.error('Error loading chat info:', error);
    }
  }

  function renderChatInfoPanel(data) {
    const infoPanel = document.getElementById('chat-info-panel');
    if (!infoPanel) return;

    infoPanel.classList.remove('hidden');
    infoPanel.style.display = 'flex';

    const conversation = data.conversation;
    const participants = data.participants || [];
    const files = data.files || [];

    const initials = conversation.name 
      ? conversation.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      : 'CH';

    const participantsHTML = participants.length > 0 ? participants.map(p => `
      <div class="participant-item">
        <div class="participant-avatar">
          <span class="avatar-initials">${escapeHtml(p.initials || 'U')}</span>
        </div>
        <div class="participant-info">
          <div class="main-info">
            <div class="name-row">
              ${(function() {
                const canView = document.querySelector('.fab-side-panel')?.getAttribute('data-can-view-profiles') === 'true';
                const isMe = Number(p.id) === Number(getChatCurrentUserId());
                // no profile link for a deactivated participant — the panel
                // shows them as "Deactivated User".
                if (canView && !isMe && !p.is_deactivated) {
                  const profilePath = p.is_contractor ? `/contractors/${p.id}` : `/teams/${p.id}`;
                  return `<a class="participant-name participant-name-link" href="${profilePath}" target="_blank">${escapeHtml(p.name)}</a>`;
                }
                return `<div class="participant-name">${escapeHtml(p.name)}</div>`;
              })()}
              ${Number(p.id) !== Number(getChatCurrentUserId()) && !p.is_deactivated ? `
              <button class="chat-btn" type="button" data-action="start-direct-chat" data-user-id="${p.id}">
                <span>Chat</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
              ` : ''}
            </div>
            <div class="participant-role">${escapeHtml(p.role || 'User')}</div>
          </div>
          <div class="participant-contact-details">
            ${p.email ? `
              <div class="contact-detail">
                <span class="contact-label">Email:</span>
                <span class="contact-value">${escapeHtml(p.email)}</span>
              </div>
            ` : ''}
            ${p.phone ? `
              <div class="contact-detail">
                <span class="contact-label">Phone:</span>
                <span class="contact-value">${escapeHtml(p.phone)}</span>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `).join('') : '<div class="no-participants">No participants found</div>';

    const filesHTML = files.length > 0 ? files.map(f => `
      <div class="file-item" data-file-id="${f.id}">
        <div class="file-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
        </div>
        <div class="file-info">
          <div class="file-name">${escapeHtml(f.file_name)}</div>
          <div class="file-meta">
            <span>${escapeHtml(f.uploaded_by)}</span>
            <span class="meta-separator"> • </span>
            <span>${formatFileDate(f.uploaded_at)}</span>
            <span class="meta-separator"> • </span>
            <span>${f.file_size || 'Unknown size'}</span>
          </div>
        </div>
        <div class="file-actions">
          <a href="${f.file_url}" target="_blank" rel="noopener noreferrer" download="${escapeHtml(f.file_name)}" class="download-btn" title="Download">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </a>
        </div>
      </div>
    `).join('') : '<div class="no-files">No files shared yet</div>';

    const actionButtonsHTML = conversation.conversation_type === 'direct' || conversation.type === 'direct' ? `
      <button class="action-button delete-btn" type="button" data-action="delete-conversation" data-conversation-id="${conversation.id}" data-conversation-name="${escapeHtml(conversation.name || 'Chat')}">
        Delete Chat
      </button>
    ` : `
      <button class="action-button leave-btn" type="button" data-action="leave-conversation" data-conversation-id="${conversation.id}" data-conversation-name="${escapeHtml(conversation.name || 'Group')}">
        Leave Group Chat
      </button>
    `;

    infoPanel.innerHTML = `
      <div class="chat-info-title">
        <p>Chat Information</p>
        <button class="chat-info-close" type="button" data-action="close-info">×</button>
      </div>
      <div class="chat-info-header">
        <div class="chat-info-tabs">
          <button class="chat-info-tab active" data-tab="details">Details</button>
          <button class="chat-info-tab" data-tab="files">Files</button>
        </div>
      </div>
      <div class="chat-info-content">
        <div id="chat-info-details" class="chat-info-tab-content active">
          <div class="info-section">
            <div class="chat-info-logo">
              <div class="logo-circle">${initials}</div>
            </div>
            ${(function() {
              const canView = document.querySelector('.fab-side-panel')?.getAttribute('data-can-view-profiles') === 'true';
              const isDirect = conversation.conversation_type === 'direct';
              if (isDirect && canView && conversation.other_user_id && !conversation.other_user_deactivated) {
                const profilePath = conversation.other_user_is_contractor ? `/contractors/${conversation.other_user_id}` : `/teams/${conversation.other_user_id}`;
                return `<a class="chat-info-name chat-info-name-link" href="${profilePath}" target="_blank">${escapeHtml(conversation.name || 'Chat')}</a>`;
              }
              return `<div class="chat-info-name">${escapeHtml(conversation.name || 'Chat')}</div>`;
            })()}
            <div class="chat-info-description">${escapeHtml(conversation.category || 'Chat conversation')}</div>
          </div>
          ${conversation.conversation_type === 'group_chat' ? `
            <div class="chat-info-section">
              <div class="section-header">
                <span class="section-title">Participants</span>
                <span class="participant-count">${participants.length}</span>
              </div>
              <button class="add-members-btn" type="button" data-action="add-members" data-conversation-id="${conversation.id}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Add Members
              </button>
              <div class="participants-list">
                ${participantsHTML}
              </div>
            </div>
          ` : ''}
          <div class="chat-info-actions">
            ${actionButtonsHTML}
          </div>
        </div>
        <div id="chat-info-files" class="chat-info-tab-content">
          <div class="files-list">
            ${filesHTML}
          </div>
        </div>
      </div>
    `;
  }

  function showDeleteChatModal(conversationId, conversationName) {
    const modal = document.getElementById('deleteChatConfirmationModal');
    if (!modal) return;

    const personNameEl = document.getElementById('delete-chat-person-name');
    if (personNameEl) {
      personNameEl.textContent = conversationName || 'this person';
    }

    const confirmBtn = document.getElementById('confirm-delete-chat');
    if (confirmBtn) {
      const newConfirmBtn = confirmBtn.cloneNode(true);
      confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
      
      newConfirmBtn.addEventListener('click', async () => {
        try {
          await ChatAPI.deleteConversation(conversationId);
          hideDeleteChatModal();
          const detailPanel = document.getElementById('conversation-detail-panel');
          const infoPanel = document.getElementById('chat-info-panel');
          if (detailPanel) detailPanel.classList.add('hidden');
          if (infoPanel) infoPanel.classList.add('hidden');
          loadConversations();
        } catch (error) {
          console.error('Error deleting conversation:', error);
          alert('Failed to delete conversation. Please try again.');
        }
      });
    }

    modal.style.display = 'flex';
  }
  
  function hideDeleteChatModal() {
    const modal = document.getElementById('deleteChatConfirmationModal');
    if (modal) {
      modal.style.display = 'none';
    }
  }
  
  // Close delete modal handlers
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="close-delete-modal"]')) {
      hideDeleteChatModal();
    }
    // Handle backdrop click for delete modal specifically
    const deleteModal = document.getElementById('deleteChatConfirmationModal');
    if (deleteModal && deleteModal.style.display === 'flex' && e.target.closest('#deleteChatConfirmationModal .chat-confirmation-backdrop')) {
      hideDeleteChatModal();
    }
  });

  function showLeaveGroupModal(conversationId, conversationName) {
    const modal = document.getElementById('leaveGroupConfirmationModal');
    if (!modal) return;

    const groupNameEl = document.getElementById('leave-group-chat-name');
    if (groupNameEl) {
      groupNameEl.textContent = conversationName || 'this group';
    }

    const confirmBtn = document.getElementById('confirm-leave-group');
    if (confirmBtn) {
      const newConfirmBtn = confirmBtn.cloneNode(true);
      confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
      
      newConfirmBtn.addEventListener('click', async () => {
        try {
          await ChatAPI.leaveConversation(conversationId);
          hideLeaveGroupModal();
          const detailPanel = document.getElementById('conversation-detail-panel');
          const infoPanel = document.getElementById('chat-info-panel');
          if (detailPanel) {
            detailPanel.classList.add('hidden');
            detailPanel.removeAttribute('data-conversation-id');
            detailPanel.innerHTML = '';
          }
          if (infoPanel) infoPanel.classList.add('hidden');
          const sidePanel = document.querySelector('.fab-side-panel');
          if (sidePanel) sidePanel.classList.add('chat-list-only');
          ChatState.currentConversationId = null;
          ChatState.clearCache();
          loadConversations();
        } catch (error) {
          console.error('Error leaving conversation:', error);
          alert('Failed to leave conversation. Please try again.');
        }
      });
    }

    modal.style.display = 'flex';
  }
  
  function hideLeaveGroupModal() {
    const modal = document.getElementById('leaveGroupConfirmationModal');
    if (modal) {
      modal.style.display = 'none';
    }
  }
  
  // Close leave group modal handlers
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="close-leave-modal"]')) {
      hideLeaveGroupModal();
    }
    // Also handle backdrop click for leave modal specifically
    const leaveModal = document.getElementById('leaveGroupConfirmationModal');
    if (leaveModal && leaveModal.style.display === 'flex' && e.target.closest('#leaveGroupConfirmationModal .chat-confirmation-backdrop')) {
      hideLeaveGroupModal();
    }
  });


  document.addEventListener('click', (e) => {
    const tab = e.target.closest('.chat-info-tab');
    if (tab) {
      const tabName = tab.getAttribute('data-tab');
      const infoPanel = document.getElementById('chat-info-panel');
      if (infoPanel && tabName) {
        infoPanel.querySelectorAll('.chat-info-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        
        infoPanel.querySelectorAll('.chat-info-tab-content').forEach(content => {
          content.classList.remove('active');
        });
        const targetContent = document.getElementById(`chat-info-${tabName}`);
        if (targetContent) {
          targetContent.classList.add('active');
        }
      }
    }
  });

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-action="close-info"]');
    if (closeBtn) {
      const infoPanel = document.getElementById('chat-info-panel');
      if (infoPanel) {
        infoPanel.classList.add('hidden');
        infoPanel.innerHTML = '';
        infoPanel.style.display = 'none';
        
        const detailPanel = document.getElementById('conversation-detail-panel');
        const sidePanel = infoPanel.closest('.fab-side-panel');
        if (sidePanel) {
          if (detailPanel && !detailPanel.classList.contains('hidden')) {
            sidePanel.style.width = '800px';
            sidePanel.style.maxWidth = '90vw';
          } else {
            sidePanel.style.width = '';
            sidePanel.style.maxWidth = '';
          }
        }
      }
    }
  });

  // Add Members button handler
  document.addEventListener('click', async (e) => {
    const addMembersBtn = e.target.closest('[data-action="add-members"]')
    if (!addMembersBtn) return

    const conversationId = addMembersBtn.dataset.conversationId
    if (!conversationId) return

    addMembersBtn.disabled = true
    addMembersBtn.textContent = 'Loading...'

    try {
      const users = await ChatAPI.fetchEligibleMembers(conversationId)
      showAddMembersModal(conversationId, users)
    } catch (error) {
      console.error('Error loading eligible members:', error)
      alert('Failed to load eligible members. Please try again.')
    } finally {
      addMembersBtn.disabled = false
      addMembersBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="8.5" cy="7" r="4"/>
          <line x1="20" y1="8" x2="20" y2="14"/>
          <line x1="23" y1="11" x2="17" y2="11"/>
        </svg>
        Add Members`
    }
  })

  function showAddMembersModal(conversationId, users) {
    let existingModal = document.getElementById('addMembersModal')
    if (existingModal) existingModal.remove()

    const usersHTML = users.length > 0 ? users.map(u => `
      <label class="add-member-item" data-name="${escapeHtml(u.name.toLowerCase())}" data-role="${escapeHtml((u.role || '').toLowerCase())}" style="display:flex;align-items:center;padding:10px 12px;gap:12px;border-bottom:1px solid #E6EAF0;cursor:pointer;border-radius:8px;transition:background 0.15s;">
        <input class="add-member-checkbox" type="checkbox" value="${u.id}" style="width:18px;height:18px;cursor:pointer;accent-color:#5065F6;flex-shrink:0;">
        <div class="participant-avatar" style="width:40px;height:40px;border-radius:50%;background-color:#596579;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <span class="avatar-initials" style="font-size:13px;color:white;font-weight:600;">${escapeHtml(u.initials || 'U')}</span>
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:500;font-size:14px;color:#111;margin-bottom:2px;">${escapeHtml(u.name)}</div>
          <div style="font-size:12px;color:#596579;">${escapeHtml(u.role || 'User')}${u.city ? ' &bull; ' + escapeHtml(u.city) : ''}</div>
        </div>
      </label>
    `).join('') : '<div style="padding:24px;text-align:center;color:#888;font-size:14px;">No eligible members to add</div>'

    // Create backdrop separately
    const backdrop = document.createElement('div')
    backdrop.id = 'addMembersBackdrop'
    backdrop.className = 'modal-backdrop fade show'
    document.body.appendChild(backdrop)

    const modal = document.createElement('div')
    modal.id = 'addMembersModal'
    modal.className = 'modal fade show'
    modal.style.cssText = 'display:block;position:fixed;inset:0;z-index:1050;overflow-y:auto;'
    modal.setAttribute('tabindex', '-1')
    modal.innerHTML = `
      <div class="modal-dialog modal-dialog-centered" style="max-width:480px;margin:1.75rem auto;">
        <div class="modal-content" style="border-radius:12px;overflow:hidden;">
          <div class="modal-header" style="padding:16px 20px;border-bottom:1px solid #e5e7eb;">
            <h5 class="modal-title" style="font-size:16px;font-weight:600;margin:0;">Add Members</h5>
            <button type="button" class="btn-close" data-action="close-add-members" aria-label="Close" style="background:none;border:none;font-size:20px;cursor:pointer;">&times;</button>
          </div>
          <div style="padding:12px 20px 0;">
            <input type="text" id="add-members-search" class="form-control" placeholder="Search members..." style="width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;">
          </div>
          <div class="modal-body" style="padding:12px 20px;max-height:350px;overflow-y:auto;">
            <div id="add-members-list" class="participants-select-list">
              ${usersHTML}
            </div>
          </div>
          <div class="modal-footer" style="padding:12px 20px;border-top:1px solid #e5e7eb;display:flex;justify-content:flex-end;gap:8px;">
            <button type="button" class="btn btn-light" data-action="close-add-members" style="padding:8px 16px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer;">Cancel</button>
            <button type="button" class="btn btn-primary btn-confirm-add-members" data-conversation-id="${conversationId}" style="padding:8px 16px;border-radius:8px;border:none;background:#4f46e5;color:#fff;cursor:pointer;" ${users.length === 0 ? 'disabled' : ''}>Add Selected</button>
          </div>
        </div>
      </div>
    `
    document.body.appendChild(modal)
    document.body.classList.add('modal-open')

    // Search filtering
    const searchInput = modal.querySelector('#add-members-search')
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const query = searchInput.value.toLowerCase().trim()
        modal.querySelectorAll('.add-member-item').forEach(item => {
          const name = item.dataset.name || ''
          const role = item.dataset.role || ''
          item.style.display = (name.includes(query) || role.includes(query)) ? '' : 'none'
        })
      })
    }
  }

  // Close Add Members modal
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-action="close-add-members"]')
    if (closeBtn) {
      const modal = document.getElementById('addMembersModal')
      const backdrop = document.getElementById('addMembersBackdrop')
      if (modal) modal.remove()
      if (backdrop) backdrop.remove()
      document.body.classList.remove('modal-open')
    }
  })

  // Confirm Add Members
  let isAddingMembers = false
  document.addEventListener('click', async (e) => {
    const confirmBtn = e.target.closest('.btn-confirm-add-members')
    if (!confirmBtn || isAddingMembers) return

    const conversationId = confirmBtn.dataset.conversationId
    const checkedBoxes = document.querySelectorAll('#addMembersModal .add-member-checkbox:checked')
    const userIds = Array.from(checkedBoxes).map(cb => parseInt(cb.value))

    if (userIds.length === 0) {
      alert('Please select at least one member to add.')
      return
    }

    isAddingMembers = true
    confirmBtn.disabled = true
    confirmBtn.textContent = 'Adding...'

    try {
      await ChatAPI.addMembers(conversationId, userIds)

      // Close modal
      const modal = document.getElementById('addMembersModal')
      const backdrop = document.getElementById('addMembersBackdrop')
      if (modal) modal.remove()
      if (backdrop) backdrop.remove()
      document.body.classList.remove('modal-open')

      // Refresh the info panel
      const data = await ChatAPI.getConversationDetails(conversationId)
      renderChatInfoPanel(data)

      // Refresh conversation list
      ChatState.clearCache()
      await loadConversations(true)
    } catch (error) {
      console.error('Error adding members:', error)
      alert(error.message || 'Failed to add members. Please try again.')
    } finally {
      isAddingMembers = false
      if (confirmBtn) {
        confirmBtn.disabled = false
        confirmBtn.textContent = 'Add Selected'
      }
    }
  })

  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-action="close-detail"]');
    if (closeBtn) {
      const detailPanel = document.getElementById('conversation-detail-panel');
      if (detailPanel) {
        detailPanel.classList.add('hidden');
        detailPanel.innerHTML = '';
        detailPanel.removeAttribute('data-conversation-id');
        detailPanel.style.flex = '0 0 0';
        detailPanel.style.minWidth = '0';

        // Clear conversation state so reopening the panel doesn't show a blank detail
        ChatState.currentConversationId = null;
        if (ChatState.currentSubscribedConversationId) {
          ChatChannel.unsubscribe(ChatState.currentSubscribedConversationId);
          ChatState.currentSubscribedConversationId = null;
        }

        const infoPanel = document.getElementById('chat-info-panel');
        if (infoPanel && !infoPanel.classList.contains('hidden')) {
          infoPanel.classList.add('hidden');
          infoPanel.innerHTML = '';
          infoPanel.style.display = 'none';
        }

        const sidePanel = detailPanel.closest('.fab-side-panel');
        if (sidePanel) {
          sidePanel.style.width = '';
          sidePanel.style.maxWidth = '';
        }

        document.querySelectorAll('.conversation-item').forEach(item => {
          item.classList.remove('active');
        });
      }
    }
  });

  document.addEventListener('click', (e) => {
    const sendBtn = e.target.closest('#conversation-send-btn');
    if (sendBtn) {
      const input = document.getElementById('conversation-input');
      if (input && input.value.trim()) {
        sendMessage(convertMentionsForStorage(input.value.trim()));
        pendingMentions = [];
      }
    }
  });

  document.addEventListener('keypress', (e) => {
    if (e.target.id === 'conversation-input' && e.key === 'Enter' && !e.shiftKey) {
      // Don't send message if mention dropdown is active (Enter selects mention)
      if (mentionState.active && document.querySelector('.chat-mention-dropdown.visible')) return;
      e.preventDefault();
      const input = e.target;
      if (input.value.trim()) {
        sendMessage(convertMentionsForStorage(input.value.trim()));
        pendingMentions = [];
      }
    }
  });

  async function sendMessage(text, imageUrl = null, attachments = []) {
    const detailPanel = document.getElementById('conversation-detail-panel');
    let conversationId = detailPanel?.getAttribute('data-conversation-id');
    if (!conversationId || conversationId === '0') {
      conversationId = ChatState.currentConversationId != null ? String(ChatState.currentConversationId) : null;
    }
    if (!conversationId || conversationId === '0') {
      console.error('No conversation ID found');
      return;
    }

    const messageData = {
      conversation_id: parseInt(conversationId, 10),
      content: text || null,
      attachments: attachments || []
    };

    if (imageUrl) {
      if (imageUrl.startsWith('data:')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        messageData.attachments.push({
          file: blob,
          file_name: 'image.png',
          file_type: blob.type || 'image/png',
          file_size: blob.size,
          attachment_type: 'image'
        });
      } else {
        messageData.attachments.push({
          file: imageUrl,
          file_name: 'image.png',
          file_type: 'image/png',
          file_size: 0,
          attachment_type: 'image'
        });
      }
    }

    // Always use API for reliability - WebSocket is used for receiving messages only
    await sendMessageViaAPI(messageData);

    const input = document.getElementById('conversation-input');
    if (input) input.value = '';
    pendingMentions = [];
    hideMentionDropdown();
  }

  async function sendMessageViaAPI(messageData) {
    const hasFiles = (messageData.attachments || []).some(a => a && (a.file instanceof File || a.file instanceof Blob));
    if (hasFiles) setChatSendingState(true, 0);

    const onProgress = hasFiles ? (percent) => setChatSendingState(true, percent) : null;

    try {
      const result = await ChatAPI.sendMessage(
        messageData.conversation_id,
        messageData.content,
        messageData.attachments || [],
        onProgress
      );

      if (result.message) {
        appendMessageToUI(result.message);
        ChatAPI.markAllRead(messageData.conversation_id)
          .then(() => ChatAPI.getNotifications())
          .then((data) => {
            updateNotificationBadge(data.unread_count || 0);
            window.updateTabUnreadBadges && window.updateTabUnreadBadges(
              data.first_tab_unread || 0,
              data.second_tab_unread || 0
            );
          })
          .catch(() => {})
          .finally(() => loadConversations());
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const msg = (error && error.message) ? error.message : 'Failed to send message. Please try again.';
      alert(msg);
    } finally {
      if (hasFiles) setChatSendingState(false);
    }
  }

  function resolveAttachmentImageUrl(attachment) {
    const f = attachment.file;
    if (typeof f === 'string' && f) return f.startsWith('/') || f.startsWith('http') ? f : `/${f}`;
    if (f && typeof f === 'object' && f.url) return f.url;
    const u = attachment.thumbnail_url || attachment.url || '';
    if (!u) return '';
    return typeof u === 'string' && !u.startsWith('/') && !u.startsWith('http') ? `/${u}` : u;
  }

  // Strip @[Name](id) mention format to plain @Name for previews
  function stripMentions(content) {
    if (!content) return '';
    return content.replace(/@\[([^\]]+)\]\(\d+\)/g, '@$1');
  }

  // Parse @[Name](id) mention format into HTML with highlighted chips
  function renderMessageContent(content) {
    if (!content) return '';
    const mentionPattern = /@\[([^\]]+)\]\((\d+)\)/g;
    let result = '';
    let lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(content)) !== null) {
      result += escapeHtml(content.slice(lastIndex, match.index));
      const name = escapeHtml(match[1]);
      const userId = match[2];
      result += `<span class="chat-mention" data-user-id="${userId}">@${name}</span>`;
      lastIndex = mentionPattern.lastIndex;
    }

    result += escapeHtml(content.slice(lastIndex));
    return result;
  }

  // ── @mention dropdown support ──

  let mentionState = { active: false, startPos: 0, query: '', activeIndex: 0 };
  let pendingMentions = []; // [{name, id}] — tracked for converting display text to storage format on send

  async function loadConversationParticipants(conversationId) {
    if (ChatState.conversationParticipants[conversationId]) return;
    try {
      const data = await ChatAPI.getConversationDetails(conversationId);
      if (data && data.participants) {
        const currentUserId = getChatCurrentUserId();
        ChatState.conversationParticipants[conversationId] = data.participants
          .filter(p => Number(p.id) !== Number(currentUserId))
          .map(p => ({ id: p.id, name: p.name, initials: p.initials, role: p.role }));
      }
    } catch (e) {
      console.error('Error loading participants for mentions:', e);
    }
  }

  function getMentionDropdown() {
    let dropdown = document.querySelector('.chat-mention-dropdown');
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'chat-mention-dropdown';
      const inputContainer = document.querySelector('.conversation-input-container');
      if (inputContainer) inputContainer.appendChild(dropdown);
    }
    return dropdown;
  }

  function showMentionDropdown(participants) {
    const dropdown = getMentionDropdown();
    if (!participants.length) {
      dropdown.classList.remove('visible');
      return;
    }
    dropdown.innerHTML = participants.map((p, i) => `
      <div class="mention-item${i === mentionState.activeIndex ? ' active' : ''}" data-user-id="${p.id}" data-user-name="${escapeHtml(p.name)}">
        <div class="mention-avatar">${escapeHtml(p.initials || p.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2))}</div>
        <div class="mention-info">
          <div class="mention-name">${escapeHtml(p.name)}</div>
          <div class="mention-role">${escapeHtml(p.role || '')}</div>
        </div>
      </div>
    `).join('');
    dropdown.classList.add('visible');
  }

  function hideMentionDropdown() {
    const dropdown = document.querySelector('.chat-mention-dropdown');
    if (dropdown) dropdown.classList.remove('visible');
    mentionState.active = false;
  }

  function getFilteredParticipants() {
    const conversationId = ChatState.currentConversationId;
    if (!conversationId) return [];
    const participants = ChatState.conversationParticipants[conversationId] || [];
    if (!mentionState.query) return participants;
    const q = mentionState.query.toLowerCase();
    return participants.filter(p => p.name.toLowerCase().includes(q));
  }

  function insertMention(input, participant) {
    const before = input.value.slice(0, mentionState.startPos);
    const after = input.value.slice(input.selectionStart);
    const displayText = `@${participant.name} `;
    input.value = before + displayText + after;
    const cursorPos = before.length + displayText.length;
    input.setSelectionRange(cursorPos, cursorPos);
    input.focus();
    // Track mention so we can convert to storage format on send
    if (!pendingMentions.find(m => m.id === participant.id)) {
      pendingMentions.push({ name: participant.name, id: participant.id });
    }
    hideMentionDropdown();
  }

  // Convert display text (@Name) to storage format (@[Name](id)) using tracked mentions
  function convertMentionsForStorage(text) {
    let result = text;
    // Replace longest names first to avoid partial matches
    const sorted = [...pendingMentions].sort((a, b) => b.name.length - a.name.length);
    for (const mention of sorted) {
      result = result.replace(new RegExp(`@${mention.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'), `@[${mention.name}](${mention.id})`);
    }
    return result;
  }

  // Listen for input events on the chat textarea (event delegation)
  document.addEventListener('input', (e) => {
    if (e.target.id !== 'conversation-input') return;
    const input = e.target;
    const cursorPos = input.selectionStart;
    const textBefore = input.value.slice(0, cursorPos);

    // Find the last @ that starts a mention (preceded by start-of-string or whitespace)
    const mentionMatch = textBefore.match(/(?:^|\s)@([^\s]*)$/);
    if (mentionMatch) {
      mentionState.active = true;
      mentionState.startPos = cursorPos - mentionMatch[1].length - 1; // position of @
      mentionState.query = mentionMatch[1];
      mentionState.activeIndex = 0;
      const filtered = getFilteredParticipants();
      showMentionDropdown(filtered);
    } else {
      hideMentionDropdown();
    }
  });

  // Keyboard navigation for mention dropdown
  document.addEventListener('keydown', (e) => {
    if (e.target.id !== 'conversation-input' || !mentionState.active) return;
    const dropdown = document.querySelector('.chat-mention-dropdown.visible');
    if (!dropdown) return;

    const items = dropdown.querySelectorAll('.mention-item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      mentionState.activeIndex = Math.min(mentionState.activeIndex + 1, items.length - 1);
      items.forEach((item, i) => item.classList.toggle('active', i === mentionState.activeIndex));
      items[mentionState.activeIndex]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      mentionState.activeIndex = Math.max(mentionState.activeIndex - 1, 0);
      items.forEach((item, i) => item.classList.toggle('active', i === mentionState.activeIndex));
      items[mentionState.activeIndex]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      const activeItem = items[mentionState.activeIndex];
      if (activeItem) {
        const userId = activeItem.getAttribute('data-user-id');
        const userName = activeItem.getAttribute('data-user-name');
        insertMention(e.target, { id: userId, name: userName });
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      hideMentionDropdown();
    }
  });

  // Click selection on mention dropdown items
  document.addEventListener('click', (e) => {
    const mentionItem = e.target.closest('.mention-item');
    if (mentionItem) {
      e.preventDefault();
      e.stopPropagation();
      const input = document.getElementById('conversation-input');
      if (input) {
        const userId = mentionItem.getAttribute('data-user-id');
        const userName = mentionItem.getAttribute('data-user-name');
        insertMention(input, { id: userId, name: userName });
      }
      return;
    }
    // Close dropdown when clicking outside
    if (!e.target.closest('.chat-mention-dropdown') && !e.target.closest('#conversation-input')) {
      hideMentionDropdown();
    }
  });

  // Select All checkbox — delegated from document so it works after DOM recreation
  document.addEventListener('change', (e) => {
    if (e.target.classList.contains('select-all-checkbox')) {
      const modal = e.target.closest('.modal');
      if (!modal) return;
      const checkboxes = modal.querySelectorAll('.participants-select-list .participant-checkbox');
      checkboxes.forEach(cb => { cb.checked = e.target.checked; });
    }
    if (e.target.classList.contains('participant-checkbox')) {
      const modal = e.target.closest('.modal');
      if (!modal) return;
      const selectAll = modal.querySelector('.select-all-checkbox');
      if (!selectAll) return;
      const allCheckboxes = modal.querySelectorAll('.participants-select-list .participant-checkbox');
      selectAll.checked = Array.from(allCheckboxes).every(cb => cb.checked);
    }
  });

  function appendMessageToUI(message) {
    const messagesContainer = document.getElementById('actual-messages');
    if (!messagesContainer) return;

    if (message.id && messagesContainer.querySelector(`[data-message-id="${message.id}"]`)) return;

    const emptyState = messagesContainer.querySelector('.empty-messages-state');
    if (emptyState) emptyState.remove();

    // System messages (e.g. "X left the group") rendered as centered notices
    if (message.message_type === 'system' || message.message_type === 4) {
      const systemWrapper = document.createElement('div');
      systemWrapper.className = 'message-wrapper system-message';
      systemWrapper.setAttribute('data-message-id', message.id);
      const notice = document.createElement('div');
      notice.className = 'system-message-notice';
      notice.textContent = message.content || '';
      systemWrapper.appendChild(notice);
      messagesContainer.appendChild(systemWrapper);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      return;
    }

    const isUserMessage = Number(message.user_id) === Number(getChatCurrentUserId());
    if (!isUserMessage) clearUndoButtons(messagesContainer);
    const isUnread = !isUserMessage; // Incoming messages from others are unread until user marks as read
    const messageWrapper = document.createElement('div');
    messageWrapper.className = `message-wrapper ${isUserMessage ? 'user-message' : 'other-message'}${isUnread ? ' message-wrapper--unread' : ''}`;
    messageWrapper.setAttribute('data-message-id', message.id);

    if (!isUserMessage) {
      const avatar = document.createElement('div');
      avatar.className = 'message-avatar';
      const initialsSpan = document.createElement('span');
      initialsSpan.className = 'avatar-initials';
      const first = message.user?.first_name?.[0] || '';
      const last = message.user?.last_name?.[0] || '';
      // chat_display_initials is 'DU' for deactivated accounts — see User#chat_display_name.
      initialsSpan.textContent = message.user?.chat_display_initials || (first + last).trim() || message.user?.email?.[0]?.toUpperCase() || 'U';
      avatar.appendChild(initialsSpan);
      messageWrapper.appendChild(avatar);
    }

    const messageContent = document.createElement('div');
    messageContent.className = 'message-content';

    const header = document.createElement('div');
    header.className = 'message-header';
    const senderName = isUserMessage ? 'You' : (message.user?.chat_display_name || message.user?.full_name || message.user?.email || 'Unknown');
    const canViewProfiles = document.querySelector('.fab-side-panel')?.getAttribute('data-can-view-profiles') === 'true';
    // a deactivated sender shows as "Deactivated User" — linking the label to
    // their profile would hand the name straight back.
    const showProfileLink = !isUserMessage && canViewProfiles && message.user_id && message.user?.chat_deactivated !== true;

    let sender;
    if (showProfileLink) {
      sender = document.createElement('a');
      sender.className = 'message-sender message-sender--top message-sender-link';
      const isContractor = message.user?.kind === 'contractor' || message.user?.kind === 2;
      sender.href = isContractor ? `/contractors/${message.user_id}` : `/teams/${message.user_id}`;
      sender.target = '_blank';
      sender.textContent = senderName;
    } else {
      sender = document.createElement('span');
      sender.className = 'message-sender message-sender--top';
      sender.textContent = senderName;
    }
    const time = document.createElement('span');
    time.className = 'message-time message-time--top';
    time.textContent = formatMessageTime(message.created_at);
    // Exact date + time on hover (relative label stays as the visible text).
    if (message.created_at) time.setAttribute('data-tooltip', formatFullDateTime(message.created_at));
    header.appendChild(sender);
    header.appendChild(time);
    const undoButton = buildUndoButton(message, getChatCurrentUserId());
    if (undoButton) header.appendChild(undoButton);
    messageContent.appendChild(header);

    const messageBubble = document.createElement('div');
    messageBubble.className = `message-bubble ${isUserMessage ? 'user-bubble' : 'other-bubble'}`;

    if (message.chat_message_attachments && message.chat_message_attachments.length > 0) {
      message.chat_message_attachments.forEach(attachment => {
        const isImage = attachment.attachment_type === 'image' || attachment.attachment_type === 0;
        
        if (isImage) {
          const src = resolveAttachmentImageUrl(attachment);
          if (!src) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'message-media-wrapper';
          const img = document.createElement('img');
          img.className = 'message-image clickable-media';
          img.src = src;
          img.alt = attachment.file_name || 'Image';
          img.loading = 'lazy';
          img.setAttribute('data-media-type', 'image');
          img.setAttribute('data-media-url', src);
          img.setAttribute('data-media-name', attachment.file_name || 'Image');
          img.onerror = function () {
            this.style.display = 'none';
            const fallback = document.createElement('span');
            fallback.className = 'message-attachment-fallback';
            fallback.textContent = attachment.file_name || 'Image';
            this.parentNode.appendChild(fallback);
          };
          wrapper.appendChild(img);
          messageBubble.appendChild(wrapper);
        } else if (attachment.attachment_type === 'video' || attachment.attachment_type === 1) {
          const src = attachment.url || resolveAttachmentImageUrl(attachment);
          if (!src) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'message-media-wrapper video-wrapper';
          const video = document.createElement('video');
          video.className = 'message-video clickable-media';
          video.setAttribute('preload', 'metadata');
          video.setAttribute('data-media-type', 'video');
          video.setAttribute('data-media-url', src);
          video.setAttribute('data-media-name', attachment.file_name || 'Video');
          const source = document.createElement('source');
          source.src = src;
          source.type = attachment.file_type || 'video/mp4';
          video.appendChild(source);
          const overlay = document.createElement('div');
          overlay.className = 'video-play-overlay';
          overlay.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>';
          wrapper.appendChild(video);
          wrapper.appendChild(overlay);
          messageBubble.appendChild(wrapper);
        } else {
          // Render video, document, or any unknown type as file attachment
          const fileUrl = attachment.url || resolveAttachmentImageUrl(attachment) || '#';
          const fileName = attachment.file_name || 'file';
          
          const fileLink = document.createElement('a');
          fileLink.className = 'message-file-attachment';
          fileLink.href = fileUrl;
          fileLink.target = '_blank';
          fileLink.style.cssText = 'cursor: pointer; display: flex; align-items: center; gap: 12px; padding: 12px 16px; background-color: #EEF0FF; border-radius: 8px; margin-bottom: 8px; text-decoration: none; color: inherit; border: 1px solid #D0D5DD;';
          
          const iconSpan = document.createElement('span');
          iconSpan.style.cssText = 'flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; background-color: #444CE7; border-radius: 8px; font-size: 20px;';
          iconSpan.textContent = '⬇';
          
          const infoSpan = document.createElement('span');
          infoSpan.style.cssText = 'display: flex; flex-direction: column; gap: 2px; overflow: hidden; flex: 1;';
          
          const nameSpan = document.createElement('span');
          nameSpan.style.cssText = 'font-size: 14px; font-weight: 500; color: #1D2939; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
          nameSpan.textContent = fileName;
          
          const typeSpan = document.createElement('span');
          typeSpan.style.cssText = 'font-size: 12px; color: #667085; text-transform: uppercase;';
          const fileType = attachment.file_type || '';
          typeSpan.textContent = fileType.split('/').pop()?.toUpperCase() || 'FILE';
          
          infoSpan.appendChild(nameSpan);
          infoSpan.appendChild(typeSpan);
          fileLink.appendChild(iconSpan);
          fileLink.appendChild(infoSpan);
          messageBubble.appendChild(fileLink);
        }
      });
    }

    if (message.content) {
      const text = document.createElement('p');
      text.className = 'message-text';
      text.innerHTML = renderMessageContent(message.content);
      messageBubble.appendChild(text);
    }

    messageContent.appendChild(messageBubble);
    messageWrapper.appendChild(messageContent);
    messagesContainer.appendChild(messageWrapper);

    const conversationMessages = document.getElementById('conversation-messages');
    if (conversationMessages) {
      conversationMessages.scrollTop = conversationMessages.scrollHeight;
    }
  }

  function subscribeToConversation(conversationId) {
    const previousId = ChatState.currentSubscribedConversationId;
    if (previousId && String(previousId) !== String(conversationId)) {
      ChatChannel.unsubscribe(previousId);
    }
    ChatState.currentSubscribedConversationId = null;

    if (!conversationId) return;

    ChatState.currentSubscribedConversationId = conversationId;
    currentChatSubscription = ChatChannel.subscribe(conversationId, {
      onConnected: () => {},
      onDisconnected: () => {},
      onMessageUndone: (data) => {
        removeUndoneMessage(data.message_id);
        loadConversations(true);
      },
      // Messages broadcast while the socket was down are never replayed
      onReconnected: () => {
        ChatAPI.fetchMessages(conversationId).then((data) => {
          const detailPanel = document.getElementById('conversation-detail-panel');
          if (detailPanel?.getAttribute('data-conversation-id') !== String(conversationId)) return;
          (data.messages || []).forEach(appendMessageToUI);
        }).catch(() => {});
        loadConversations();
      },
      onNewMessage: (data) => {
        const message = data.message || data;
        appendMessageToUI(message);

        if (data.online_count !== undefined) {
          const statusElement = document.getElementById(`online-status-${conversationId}`);
          if (statusElement) {
            statusElement.textContent = `${data.online_count} Online`;
          }
        }

        const isFromMe = Number(message.user_id) === Number(getChatCurrentUserId());
        const activeId = getActivelyViewedConversationId();
        const isActivelyViewing = activeId != null && String(activeId) === String(conversationId);

        if (!isFromMe) {
          // Don't mark as read here — the click handler (markCurrentConversationAsRead)
          // handles that when the user interacts with the panel.
          // NotificationChannel handles sound, badges, and message display.
          loadConversations();
        } else {
          loadConversations();
        }
      },
      onPresence: (data) => {
        if (data.online_count !== undefined) {
          const statusElement = document.getElementById(`online-status-${conversationId}`);
          if (statusElement) {
            statusElement.textContent = `${data.online_count} Online`;
          }
        }
      },
      onError: (error) => console.error('Chat error:', error)
    });

    if (currentChatSubscription) {
      currentChatSubscription.conversationId = conversationId;
    }
  }
  
  // Re-establish subscriptions when panel is reopened
  window.reestablishChatSubscriptions = function() {
    const detailPanel = document.getElementById('conversation-detail-panel');
    const conversationId = detailPanel?.getAttribute('data-conversation-id');
    
    if (conversationId && conversationId !== '0') {
      // Re-subscribe to the current conversation
      subscribeToConversation(conversationId);
    }
    
    // Re-subscribe to notifications
    subscribeToNotifications();
  };

  let notificationPollInterval = null;

  function startNotificationPolling() {
    if (notificationPollInterval) return;
    notificationPollInterval = setInterval(() => {
      ChatAPI.getNotifications().then(data => {
        updateNotificationBadge(data.unread_count || 0);
        window.updateTabUnreadBadges && window.updateTabUnreadBadges(
          data.first_tab_unread || 0,
          data.second_tab_unread || 0
        );
      }).catch(() => {});
    }, 15000); // Poll every 15 seconds as fallback
  }

  function stopNotificationPolling() {
    if (notificationPollInterval) {
      clearInterval(notificationPollInterval);
      notificationPollInterval = null;
    }
  }

  function subscribeToNotifications() {
    stopNotificationPolling();
    notificationSubscription = NotificationChannel.subscribe({
      onConnected: () => {
        stopNotificationPolling();
        ChatAPI.getNotifications().then(data => {
          updateNotificationBadge(data.unread_count || 0);
          window.updateTabUnreadBadges && window.updateTabUnreadBadges(
            data.first_tab_unread || 0,
            data.second_tab_unread || 0
          );
        });
      },
      onDisconnected: () => {
        startNotificationPolling();
      },
      // Returns true to suppress sound/browser notification for this message
      // (e.g., when user is viewing the conversation that triggered it)
      onNewNotification: (notification, rawData) => {
        const notifConvId = notification.chat_conversation_id || notification.conversation_id;
        const activeViewingId = getActivelyViewedConversationId();
        const isActivelyViewing = activeViewingId && notifConvId && String(activeViewingId) === String(notifConvId);

        const refreshBadges = () => {
          ChatAPI.getNotifications().then(data => {
            updateNotificationBadge(data.unread_count || 0);
            window.updateTabUnreadBadges && window.updateTabUnreadBadges(
              data.first_tab_unread || 0,
              data.second_tab_unread || 0
            );
            try {
              localStorage.setItem('chat_notification_sync', JSON.stringify({
                unread_count: data.unread_count || 0,
                first_tab_unread: data.first_tab_unread || 0,
                second_tab_unread: data.second_tab_unread || 0,
                play_sound: false,
                ts: Date.now()
              }));
            } catch (e) {}
          });
        };

        if (isActivelyViewing) {
          // User is looking at this conversation — display the message,
          // update badges, but DON'T mark as read here. The existing click
          // handler (markCurrentConversationAsRead) handles that when the
          // user interacts with the panel.
          if (rawData && rawData.message) {
            appendMessageToUI(rawData.message);
          }
          refreshBadges();
          loadConversations();
        } else {
          // Panel closed or viewing a different conversation — full notification
          refreshBadges();
          loadConversations();
        }

        // Suppress browser notification popup when user is looking at the conversation
        return isActivelyViewing;
      },
      onReadUpdate: () => {
        ChatAPI.getNotifications().then(data => {
          updateNotificationBadge(data.unread_count || 0);
          window.updateTabUnreadBadges && window.updateTabUnreadBadges(
            data.first_tab_unread || 0,
            data.second_tab_unread || 0
          );
          try {
            localStorage.setItem('chat_notification_sync', JSON.stringify({
              unread_count: data.unread_count || 0,
              first_tab_unread: data.first_tab_unread || 0,
              second_tab_unread: data.second_tab_unread || 0,
              play_sound: false,
              ts: Date.now()
            }));
          } catch (e) {}
        });
      },
      playSound: () => ChatState.notificationSettings.notification_sound,
      showBrowserNotification: () => ChatState.notificationSettings.allow_desktop_notifications
    });

    // Start polling as fallback — will be stopped once WebSocket connects
    startNotificationPolling();
  }

  function applyBadgeCount(count) {
    const badges = document.querySelectorAll('.fab-badge[data-chat-notification-badge], .notification-badge, [data-notification-badge], [data-chat-notification-badge]');
    badges.forEach((badge) => {
      badge.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
      badge.style.display = count > 0 ? 'flex' : 'none';
      badge.classList.toggle('has-notifications', count > 0);
    });
  }

  async function updateNotificationBadge(count) {
    applyBadgeCount(count);
  }

  // Update tab unread badges
  window.updateTabUnreadBadges = function(firstUnread, secondUnread) {
    const firstTab = document.querySelector('[data-tab="first"]');
    const secondTab = document.querySelector('[data-tab="second"]');
    
    if (firstTab) {
      let badge = firstTab.querySelector('.tab-unread-badge');
      if (firstUnread > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'tab-unread-badge';
          badge.id = 'first-tab-badge';
          firstTab.appendChild(badge);
        }
        badge.textContent = firstUnread > 99 ? '99+' : firstUnread;
        badge.style.display = '';
      } else if (badge) {
        badge.style.display = 'none';
      }
    }
    
    if (secondTab) {
      let badge = secondTab.querySelector('.tab-unread-badge');
      if (secondUnread > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'tab-unread-badge';
          badge.id = 'second-tab-badge';
          secondTab.appendChild(badge);
        }
        badge.textContent = secondUnread > 99 ? '99+' : secondUnread;
        badge.style.display = '';
      } else if (badge) {
        badge.style.display = 'none';
      }
    }
  };

  function getChatCurrentUserId() {
    const panel = document.querySelector('.fab-side-panel');
    const id = panel?.getAttribute('data-chat-user-id') ?? document.body.getAttribute('data-user-id');
    return id != null ? Number(id) : null;
  }

  // Returns the conversation ID (as string) the user is ACTUALLY viewing right now
  // (panel must be visible AND a conversation detail must be open)
  function getActivelyViewedConversationId() {
    const sidePanel = document.querySelector('.fab-side-panel');
    if (!sidePanel || !sidePanel.classList.contains('active')) return null;

    const detailPanel = document.getElementById('conversation-detail-panel');
    if (!detailPanel || detailPanel.classList.contains('hidden')) return null;

    const id = ChatState.currentConversationId
      || detailPanel.getAttribute('data-conversation-id');
    return id != null ? String(id) : null;
  }

  function renderFromCache() {
    if (ChatState.cachedConversations) {
      renderConversationsList(ChatState.cachedConversations);
      return true;
    }
    return false;
  }

  async function loadConversations(cacheBust = false) {
    try {
      const conversations = await ChatAPI.fetchConversations(cacheBust);
      ChatState.cachedConversations = conversations;
      renderConversationsList(conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }

  function getActiveChatTab() {
    const active = document.querySelector('.conversation-tabs .tab-item.active');
    return active?.getAttribute('data-tab') === 'second' ? 'second' : 'first';
  }

  function renderConversationsList(conversations) {
    const container = document.getElementById('tab-content-container');
    if (!container) return;

    const tab = getActiveChatTab();
    ChatState.currentTab = tab;
    let filteredConversations = conversations.filter((c) => {
      if (c.display_tab != null) return c.display_tab === tab;
      const category = c.category;
      if (tab === 'second') return category === 'aoka';
      return category === 'city' || category === 'applicant' || category == null;
    });
    if (ChatState.currentFilter === 'group') {
      filteredConversations = filteredConversations.filter(c => (c.conversation_type || c.type) === 'group_chat');
    } else if (ChatState.currentFilter === 'direct') {
      filteredConversations = filteredConversations.filter(c => (c.conversation_type || c.type) === 'direct');
    }

    filteredConversations.sort((a, b) => {
      const atA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const atB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return atB - atA;
    });

    if (filteredConversations.length === 0) {
      container.innerHTML = `
        <div class="empty-state">
          <svg class="empty-state-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <h3 class="empty-state-title">No Conversations</h3>
          <p class="empty-state-description">Start a new conversation to get started.</p>
        </div>
      `;
      return;
    }

    const list = document.createElement('div');
    list.className = 'conversations-list';

    filteredConversations.forEach(conversation => {
      const unreadCount = conversation.unread_count || 0;
      const isUnread = unreadCount > 0;

      const item = document.createElement('div');
      item.className = 'conversation-item' + (isUnread ? ' conversation-item--unread' : '');
      item.setAttribute('data-conversation-id', conversation.id);
      item.setAttribute('data-conversation-name', conversation.name || conversation.display_name || 'Chat');

      const conversationName = conversation.name || conversation.display_name || 'Chat';
      const initials = conversationName 
        ? conversationName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : 'CH';

      const lastMessage = conversation.last_message || {};
      const lastMessageText = lastMessage.content || '';
      const lastMessageTime = formatTimeAgo(conversation.last_message_at);
      const conversationType = conversation.conversation_type || conversation.type || 'direct';
      const isGroup = conversationType === 'group_chat' || conversationType === 'group';

      const unreadBadgeHtml = isUnread
        ? `<span class="conversation-unread-badge">${unreadCount > 99 ? '99+' : unreadCount}</span>`
        : '';

      item.innerHTML = `
        <div class="conversation-avatar">
          <span class="avatar-initials">${initials}</span>
        </div>
        <div class="conversation-details">
          <div class="conversation-header">
            <span class="conversation-title">${escapeHtml(conversationName)}</span>
            ${unreadBadgeHtml}
            ${isGroup ? `
              <svg class="title-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            ` : ''}
          </div>
          ${!isGroup && conversation.subtitle ? `
            <span class="conversation-subtitle">${escapeHtml(conversation.subtitle)}</span>
          ` : ''}
          <div class="conversation-preview">
            ${isGroup && lastMessage.user ? `${escapeHtml(lastMessage.user.chat_display_name || lastMessage.user.full_name || lastMessage.user.email)}: ` : ''}
            ${escapeHtml(stripMentions(lastMessageText))}
          </div>
          ${isGroup ? `
            <div class="conversation-meta">${conversation.participant_count || 0} Participants</div>
          ` : ''}
        </div>
        <div class="conversation-time">${lastMessageTime}</div>
      `;

      list.appendChild(item);
    });

    container.innerHTML = '';
    container.appendChild(list);

    if (ChatState.currentConversationId != null) {
      const activeId = String(ChatState.currentConversationId);
      list.querySelectorAll('.conversation-item').forEach((el) => {
        el.classList.toggle('active', el.getAttribute('data-conversation-id') === activeId);
      });
    }
  }

  let conversationListSearchTimeout = null;
  document.addEventListener('input', (e) => {
    const searchInput = e.target.closest('.search-input');
    if (searchInput && searchInput.id === 'conversation-list-search') {
      const query = searchInput.value.trim();
      if (query.length === 0) {
        if (conversationListSearchTimeout) clearTimeout(conversationListSearchTimeout);
        conversationListSearchTimeout = null;
        loadConversations();
        return;
      }
      if (query.length < 2) return;
      if (conversationListSearchTimeout) clearTimeout(conversationListSearchTimeout);
      conversationListSearchTimeout = setTimeout(async () => {
        conversationListSearchTimeout = null;
        try {
          const { category: tab } = getNewChatTabContext();
          const results = await ChatAPI.searchConversations(query, null, tab);
          if (results.results && results.results.length > 0) {
            const container = document.getElementById('tab-content-container');
            if (container) {
              const searchResultsHTML = results.results.map(result => {
                const initials = result.conversation_name 
                  ? result.conversation_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                  : 'CH';
                
                return `
                  <div class="search-result-item" data-conversation-id="${result.conversation_id}" data-message-id="${result.message_id}">
                    <div class="search-result-header">
                      <div class="conversation-avatar">
                        <span class="avatar-initials">${initials}</span>
                      </div>
                      <div class="search-result-info">
                        <div class="search-result-conversation">${escapeHtml(result.conversation_name || 'Chat')}</div>
                        <div class="search-result-meta">
                          <span>${escapeHtml(result.sender_name)}</span>
                          <span class="meta-separator"> • </span>
                          <span title="${formatFullDateTime(result.created_at)}">${formatMessageTime(result.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div class="search-result-content">
                      ${result.highlighted_content || escapeHtml(result.snippet || '')}
                    </div>
                  </div>
                `;
              }).join('');
              
              container.innerHTML = `
                <div class="search-results-container">
                  <div class="search-results-header">
                    <span>${results.results.length} result${results.results.length !== 1 ? 's' : ''} found</span>
                  </div>
                  <div class="search-results-list">
                    ${searchResultsHTML}
                  </div>
                </div>
              `;
              
              container.querySelectorAll('.search-result-item').forEach(item => {
                item.addEventListener('click', async () => {
                  const conversationId = item.getAttribute('data-conversation-id');
                  const messageId = item.getAttribute('data-message-id');
                  if (conversationId) {
                    await loadConversationDetail(parseInt(conversationId), {
                      scrollToMessageId: messageId || undefined
                    });
                  }
                });
              });
            }
          } else {
            const container = document.getElementById('tab-content-container');
            if (container) {
              container.innerHTML = '<div class="empty-state"><p>No results found</p></div>';
            }
          }
        } catch (error) {
          console.error('Error searching:', error);
        }
        }, 300);
    }
  });

  document.addEventListener('input', async (e) => {
    const searchInput = e.target.closest('.search-input');
    if (searchInput && searchInput.id === 'conversation-search-input') {
      const query = searchInput.value.trim();
      const detailPanel = document.getElementById('conversation-detail-panel');
      const conversationId = detailPanel?.getAttribute('data-conversation-id');
      
      if (query.length > 2 && conversationId) {
        try {
          const results = await ChatAPI.searchConversations(query, conversationId);
          // Highlight matching messages in the conversation
          if (results.results && results.results.length > 0) {
            highlightSearchResults(results.results, query);
          }
        } catch (error) {
          console.error('Error searching messages:', error);
        }
      } else if (query.length === 0) {
        clearSearchHighlights();
      }
    }
  });

  function highlightSearchResults(results, query) {
    const messagesContainer = document.getElementById('actual-messages');
    if (!messagesContainer) return;

    clearSearchHighlights();

    results.forEach(result => {
      const messageEl = messagesContainer.querySelector(`[data-message-id="${result.message_id}"]`);
      if (messageEl) {
        messageEl.classList.add('search-highlight');
        messageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  function clearSearchHighlights() {
    const messagesContainer = document.getElementById('actual-messages');
    if (messagesContainer) {
      messagesContainer.querySelectorAll('.search-highlight').forEach(el => {
        el.classList.remove('search-highlight');
      });
    }
  }

  document.addEventListener('click', (e) => {
    const tabItem = e.target.closest('.conversation-tabs .tab-item');
    if (tabItem) {
      e.preventDefault();
      e.stopPropagation();
      const tab = tabItem.getAttribute('data-tab');
      if (!tab) return;
      const tabs = document.querySelectorAll('.conversation-tabs .tab-item');
      tabs.forEach((t) => t.classList.remove('active'));
      tabItem.classList.add('active');
      ChatState.currentTab = tab;
      if (!renderFromCache()) loadConversations();
      updateNewChatButtonLabel();
    }
  }, true);

  document.addEventListener('click', (e) => {
    const filterTab = e.target.closest('.filter-tab');
    if (filterTab) {
      const allFilters = document.querySelectorAll('.filter-tab');
      allFilters.forEach(tab => tab.classList.remove('active'));
      filterTab.classList.add('active');

      ChatState.currentFilter = filterTab.getAttribute('data-filter') || 'all';
      if (!renderFromCache()) loadConversations();
    }
  });

  function setActiveConversationInList(conversationId) {
    const id = String(conversationId);
    const allConversations = document.querySelectorAll('.conversation-item');
    allConversations.forEach(item => {
      item.classList.remove('active');
      if (item.getAttribute('data-conversation-id') === id) {
        item.classList.add('active');
      }
    });
  }

  document.addEventListener('click', async (e) => {
    const conversationItem = e.target.closest('.conversation-item');
    if (conversationItem) {
      const allConversations = document.querySelectorAll('.conversation-item');
      allConversations.forEach(item => item.classList.remove('active'));
      conversationItem.classList.add('active');

      const conversationId = conversationItem.getAttribute('data-conversation-id');
      const conversationName = conversationItem.getAttribute('data-conversation-name');
      
      if (conversationId) {
        ChatState.currentConversationId = conversationId;
        await loadConversationDetail(parseInt(conversationId));
      }
    }
  });

  let markReadOnClickTimeout = null;
  function markCurrentConversationAsRead() {
    const detailPanel = document.getElementById('conversation-detail-panel');
    if (!detailPanel || detailPanel.classList.contains('hidden')) return;
    const conversationId = detailPanel.getAttribute('data-conversation-id') || ChatState.currentConversationId;
    if (!conversationId) return;

    ChatAPI.markAllRead(conversationId)
      .then(() => ChatAPI.getNotifications())
      .then((data) => {
        updateNotificationBadge(data.unread_count || 0);
        window.updateTabUnreadBadges && window.updateTabUnreadBadges(
          data.first_tab_unread || 0,
          data.second_tab_unread || 0
        );
      })
      .catch(() => {});

    const listItem = document.querySelector(`.conversation-item[data-conversation-id="${conversationId}"]`);
    if (listItem) {
      listItem.classList.remove('conversation-item--unread');
      const badge = listItem.querySelector('.conversation-unread-badge');
      if (badge) badge.remove();
    }

    // Update cached conversations so filter/tab switches don't restore stale badges
    if (ChatState.cachedConversations) {
      const cached = ChatState.cachedConversations.find(c => String(c.id) === String(conversationId));
      if (cached) cached.unread_count = 0;
    }

    detailPanel.querySelectorAll('.message-wrapper--unread').forEach((el) => el.classList.remove('message-wrapper--unread'));
  }

  document.addEventListener('click', (e) => {
    const detailPanel = e.target.closest('#conversation-detail-panel');
    if (!detailPanel) return;
    if (markReadOnClickTimeout) clearTimeout(markReadOnClickTimeout);
    markReadOnClickTimeout = setTimeout(() => {
      markReadOnClickTimeout = null;
      markCurrentConversationAsRead();
    }, 150);
  });

  async function loadConversationDetail(conversationId, options = {}) {
    const detailPanel = document.getElementById('conversation-detail-panel');
    if (!detailPanel) return;
    const scrollToMessageId = options.scrollToMessageId || null;

    // Show loading state immediately
    detailPanel.classList.remove('hidden');
    detailPanel.style.display = 'flex';
    detailPanel.setAttribute('data-conversation-id', String(conversationId));
    detailPanel.innerHTML = `
      <div class="conversation-detail-loading">
        <div class="loading-spinner-chat"></div>
      </div>
    `;
    const sidePanel = document.querySelector('.fab-side-panel');
    if (sidePanel) sidePanel.classList.remove('chat-list-only');

    try {
      let url = `/chats/conversation_detail.html?conversation_id=${encodeURIComponent(conversationId)}`;
      if (scrollToMessageId) url += `&scroll_to_message_id=${encodeURIComponent(scrollToMessageId)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        console.error('Failed to load conversation detail:', response.status, response.statusText);
        return;
      }

      const html = await response.text();

      // Hide panel content while we insert and position scroll
      detailPanel.style.visibility = 'hidden';
      detailPanel.innerHTML = html;

      ChatState.currentConversationId = conversationId;

      // Pre-fetch participants for @mention support in group chats
      loadConversationParticipants(conversationId);

      subscribeToConversation(conversationId);
      ChatAPI.markAllRead(conversationId)
        .then(() => ChatAPI.getNotifications())
        .then((data) => {
          updateNotificationBadge(data.unread_count || 0);
          window.updateTabUnreadBadges && window.updateTabUnreadBadges(
            data.first_tab_unread || 0,
            data.second_tab_unread || 0
          );
        })
        .catch(() => {});

      const listItem = document.querySelector(`.conversation-item[data-conversation-id="${conversationId}"]`);
      if (listItem) {
        listItem.classList.remove('conversation-item--unread');
        const badge = listItem.querySelector('.conversation-unread-badge');
        if (badge) badge.remove();
      }

      // Update cached conversations so filter/tab switches don't restore stale badges
      if (ChatState.cachedConversations) {
        const cached = ChatState.cachedConversations.find(c => String(c.id) === String(conversationId));
        if (cached) cached.unread_count = 0;
      }

      const infoPanel = document.getElementById('chat-info-panel');
      if (infoPanel && !infoPanel.classList.contains('hidden')) {
        loadChatInfo(conversationId);
      }

      const messagesContainer = detailPanel.querySelector('#conversation-messages');
      const reveal = () => { detailPanel.style.visibility = ''; };

      if (scrollToMessageId) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const messageEl = detailPanel.querySelector(`[data-message-id="${scrollToMessageId}"]`);
          if (messageEl && messagesContainer) {
            messageEl.scrollIntoView({ behavior: 'instant', block: 'center' });
            messageEl.classList.add('highlight-message');
            setTimeout(() => messageEl.classList.remove('highlight-message'), 2000);
          }
          reveal();
        }));
      } else if (messagesContainer) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
          reveal();
        }));
      } else {
        reveal();
      }
    } catch (error) {
      console.error('Error loading conversation detail:', error);
    }
  }

  function chatPerPageInit() {
    // Subscribe immediately — don't delay behind any HTTP call
    subscribeToNotifications();

    // Load notification settings in parallel (defaults are true, so sound/badges
    // work immediately; settings just refine for users who disabled them)
    ChatAPI.getChatSettings().then((settings) => {
      ChatState.notificationSettings = {
        allow_desktop_notifications: settings.allow_desktop_notifications !== false,
        notification_sound: settings.notification_sound !== false,
        email_notifications: settings.email_notifications !== false,
        notification_volume: settings.notification_volume ?? 0.5
      };
    }).catch(() => {});

    ChatAPI.getNotifications().then((data) => {
      updateNotificationBadge(data.unread_count || 0);
    }).catch(() => {});

    if (document.getElementById('tab-content-container')) {
      loadConversations();
      updateNewChatButtonLabel();
    }
  }

  chatPerPageInit();

  window.loadChatConversations = function () {
    const previousConversationId = ChatState.currentConversationId;
    ChatState.resetOnPanelOpen();
    loadConversations();
    updateNewChatButtonLabel();

    if (previousConversationId) {
      // Restore the previously open conversation
      ChatState.currentConversationId = previousConversationId;
      loadConversationDetail(previousConversationId).then(() => {
        setActiveConversationInList(previousConversationId);
      }).catch(() => {
        // Conversation may have been deleted; fall back to list-only view
        const detailPanel = document.getElementById('conversation-detail-panel');
        if (detailPanel) {
          detailPanel.classList.add('hidden');
          detailPanel.removeAttribute('data-conversation-id');
          detailPanel.innerHTML = '';
        }
        const sidePanel = document.querySelector('.fab-side-panel');
        if (sidePanel) sidePanel.classList.add('chat-list-only');
      });
    } else {
      const detailPanel = document.getElementById('conversation-detail-panel');
      if (detailPanel) {
        detailPanel.classList.add('hidden');
        detailPanel.removeAttribute('data-conversation-id');
        detailPanel.innerHTML = '';
      }
      const sidePanel = document.querySelector('.fab-side-panel');
      if (sidePanel) sidePanel.classList.add('chat-list-only');
    }
  };

  document.addEventListener('click', (e) => {
    const attachmentBtn = e.target.closest('[data-action="show-file-upload"]');
    if (attachmentBtn) {
      e.stopPropagation();
      const modal = document.getElementById('fileUploadModal');
      if (modal) {
        modal.classList.toggle('show');
      }
      return;
    }

    const fileUploadModal = e.target.closest('#fileUploadModal');
    if (!fileUploadModal) {
      const modal = document.getElementById('fileUploadModal');
      if (modal && modal.classList.contains('show')) {
        if (!e.target.closest('.file-upload-option')) {
          modal.classList.remove('show');
        }
      }
    }
  });

  // Media lightbox for images and videos with zoom
  const lightboxState = {
    scale: 1,
    minScale: 0.5,
    maxScale: 5,
    translateX: 0,
    translateY: 0,
    isDragging: false,
    startX: 0,
    startY: 0,
    currentMediaType: null
  };

  function resetLightboxZoom() {
    lightboxState.scale = 1;
    lightboxState.translateX = 0;
    lightboxState.translateY = 0;
    updateLightboxTransform();
    updateZoomControls();
  }

  function updateLightboxTransform() {
    const img = document.getElementById('lightbox-image');
    if (img) {
      img.style.transform = `translate(${lightboxState.translateX}px, ${lightboxState.translateY}px) scale(${lightboxState.scale})`;
      img.classList.toggle('zoomed', lightboxState.scale > 1);
    }
  }

  function updateZoomControls() {
    const zoomLevel = document.getElementById('lightbox-zoom-level');
    const zoomIn = document.getElementById('lightbox-zoom-in');
    const zoomOut = document.getElementById('lightbox-zoom-out');
    
    if (zoomLevel) zoomLevel.textContent = Math.round(lightboxState.scale * 100) + '%';
    if (zoomIn) zoomIn.disabled = lightboxState.scale >= lightboxState.maxScale;
    if (zoomOut) zoomOut.disabled = lightboxState.scale <= lightboxState.minScale;
  }

  function zoomLightbox(delta, centerX, centerY) {
    const img = document.getElementById('lightbox-image');
    if (!img) return;

    const oldScale = lightboxState.scale;
    lightboxState.scale = Math.min(lightboxState.maxScale, Math.max(lightboxState.minScale, lightboxState.scale + delta));
    
    if (lightboxState.scale === oldScale) return;

    // Adjust position to zoom toward cursor/center
    if (centerX !== undefined && centerY !== undefined) {
      const rect = img.getBoundingClientRect();
      const imgCenterX = rect.left + rect.width / 2;
      const imgCenterY = rect.top + rect.height / 2;
      const offsetX = centerX - imgCenterX;
      const offsetY = centerY - imgCenterY;
      const scaleDiff = lightboxState.scale / oldScale;
      lightboxState.translateX -= offsetX * (scaleDiff - 1);
      lightboxState.translateY -= offsetY * (scaleDiff - 1);
    }

    // Reset position if zoomed out to 1x or less
    if (lightboxState.scale <= 1) {
      lightboxState.translateX = 0;
      lightboxState.translateY = 0;
    }

    updateLightboxTransform();
    updateZoomControls();
  }

  function openMediaLightbox(mediaType, mediaUrl, mediaName) {
    const lightbox = document.getElementById('chat-media-lightbox');
    if (!lightbox) return;

    const lightboxImage = document.getElementById('lightbox-image');
    const lightboxVideo = document.getElementById('lightbox-video');
    const lightboxFilename = document.getElementById('lightbox-filename');
    const lightboxDownload = document.getElementById('lightbox-download');
    const zoomControls = document.getElementById('lightbox-zoom-controls');

    // Reset zoom state
    resetLightboxZoom();
    lightboxState.currentMediaType = mediaType;

    // Hide both media elements first
    if (lightboxImage) lightboxImage.style.display = 'none';
    if (lightboxVideo) {
      lightboxVideo.style.display = 'none';
      lightboxVideo.pause();
      lightboxVideo.src = '';
    }
    if (zoomControls) zoomControls.style.display = 'none';

    if (mediaType === 'image') {
      if (lightboxImage) {
        lightboxImage.src = mediaUrl;
        lightboxImage.style.display = 'block';
      }
      if (zoomControls) zoomControls.style.display = 'flex';
    } else if (mediaType === 'video') {
      if (lightboxVideo) {
        lightboxVideo.src = mediaUrl;
        lightboxVideo.style.display = 'block';
      }
    }

    if (lightboxFilename) lightboxFilename.textContent = mediaName;
    if (lightboxDownload) {
      lightboxDownload.href = mediaUrl;
      lightboxDownload.target = '_blank';
      lightboxDownload.rel = 'noopener noreferrer';
      lightboxDownload.download = mediaName;
      // Force new tab for cross-origin URLs where download attribute is ignored
      lightboxDownload.onclick = function(e) {
        e.preventDefault();
        window.open(mediaUrl, '_blank', 'noopener,noreferrer');
      };
    }

    lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function closeMediaLightbox() {
    const lightbox = document.getElementById('chat-media-lightbox');
    if (!lightbox) return;

    const lightboxVideo = document.getElementById('lightbox-video');
    if (lightboxVideo) {
      lightboxVideo.pause();
      lightboxVideo.src = '';
    }

    resetLightboxZoom();
    lightbox.style.display = 'none';
    document.body.style.overflow = '';
  }

  // Click on image/video to open lightbox
  document.addEventListener('click', (e) => {
    const media = e.target.closest('.clickable-media');
    if (media) {
      e.preventDefault();
      e.stopPropagation();
      const mediaType = media.getAttribute('data-media-type');
      const mediaUrl = media.getAttribute('data-media-url');
      const mediaName = media.getAttribute('data-media-name');
      openMediaLightbox(mediaType, mediaUrl, mediaName);
    }
  });

  // Zoom controls click handlers
  document.addEventListener('click', (e) => {
    if (e.target.closest('#lightbox-zoom-in')) {
      zoomLightbox(0.25);
    } else if (e.target.closest('#lightbox-zoom-out')) {
      zoomLightbox(-0.25);
    } else if (e.target.closest('#lightbox-zoom-reset')) {
      resetLightboxZoom();
    }
  });

  // Mouse wheel zoom
  document.addEventListener('wheel', (e) => {
    const lightbox = document.getElementById('chat-media-lightbox');
    if (!lightbox || lightbox.style.display === 'none') return;
    if (lightboxState.currentMediaType !== 'image') return;
    
    const img = document.getElementById('lightbox-image');
    if (!img || !e.target.closest('.lightbox-media-container')) return;

    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    zoomLightbox(delta, e.clientX, e.clientY);
  }, { passive: false });

  // Double click to toggle zoom
  document.addEventListener('dblclick', (e) => {
    const img = e.target.closest('#lightbox-image');
    if (!img) return;
    
    if (lightboxState.scale > 1) {
      resetLightboxZoom();
    } else {
      zoomLightbox(1, e.clientX, e.clientY);
    }
  });

  // Drag to pan when zoomed
  document.addEventListener('mousedown', (e) => {
    const img = e.target.closest('#lightbox-image');
    if (!img || lightboxState.scale <= 1) return;
    
    lightboxState.isDragging = true;
    lightboxState.startX = e.clientX - lightboxState.translateX;
    lightboxState.startY = e.clientY - lightboxState.translateY;
    img.classList.add('dragging');
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!lightboxState.isDragging) return;
    
    lightboxState.translateX = e.clientX - lightboxState.startX;
    lightboxState.translateY = e.clientY - lightboxState.startY;
    updateLightboxTransform();
  });

  document.addEventListener('mouseup', () => {
    if (lightboxState.isDragging) {
      lightboxState.isDragging = false;
      const img = document.getElementById('lightbox-image');
      if (img) img.classList.remove('dragging');
    }
  });

  // Close lightbox on backdrop click (but not when dragging)
  document.addEventListener('click', (e) => {
    if (lightboxState.isDragging) return;
    if (e.target.closest('.lightbox-backdrop') || e.target.closest('.lightbox-close')) {
      closeMediaLightbox();
    }
  });

  // Close lightbox on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeMediaLightbox();
    }
  });

  document.addEventListener('click', (e) => {
    const photosBtn = e.target.closest('[data-action="upload-photos-videos"]');
    if (photosBtn) {
      const input = document.getElementById('file-upload-input');
      if (input) {
        input.click();
      }
    }
  });

  document.addEventListener('click', (e) => {
    const documentBtn = e.target.closest('[data-action="upload-document"]');
    if (documentBtn) {
      const input = document.getElementById('document-upload-input');
      if (input) {
        input.click();
      }
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target;
    if (input.id !== 'file-upload-input' && input.id !== 'document-upload-input') return;

    const files = input.files ? Array.from(input.files) : [];
    if (files.length === 0) return;

    const detailPanel = document.getElementById('conversation-detail-panel');
    const conversationId = detailPanel?.getAttribute('data-conversation-id');
    if (!conversationId || conversationId === '0') {
      alert('Please open a conversation to send files.');
      input.value = '';
      return;
    }

    const isMedia = input.id === 'file-upload-input';
    const kind = isMedia ? 'media' : 'document';

    for (const file of files) {
      const { valid, error } = chatValidateFile(file, kind);
      if (!valid) {
        alert(error);
        input.value = '';
        return;
      }
    }

    const attachmentType = isMedia ? null : 'document'; // null => per-file type (image/video)
    const attachments = chatBuildAttachmentsFromFiles(files, attachmentType);
    const caption = attachments.length === 1
      ? (attachments[0].attachment_type === 'video' ? `📹 ${attachments[0].file_name}` : attachments[0].attachment_type === 'document' ? `📄 ${attachments[0].file_name}` : null)
      : (attachments.length > 1 ? `${attachments.length} files` : null);

    const modal = document.getElementById('fileUploadModal');
    if (modal) modal.classList.remove('show');
    input.value = '';

    try {
      await sendMessage(caption || null, null, attachments);
    } catch (err) {
      console.error('Error uploading files:', err);
      alert(err && err.message ? err.message : 'Failed to send. Please try again.');
    }
  });

  // Paste screenshots/images straight from the clipboard into the message input
  // (e.g. Cmd/Ctrl+V of a screenshot) — uploaded as image attachments.
  document.addEventListener('paste', async (e) => {
    if (!e.target || e.target.id !== 'conversation-input') return;

    const items = (e.clipboardData && e.clipboardData.items) || [];
    const imageFiles = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type && item.type.indexOf('image/') === 0) {
        const f = item.getAsFile();
        if (f) imageFiles.push(f);
      }
    }
    // No image in the clipboard — let the normal text paste happen.
    if (imageFiles.length === 0) return;
    e.preventDefault();

    const detailPanel = document.getElementById('conversation-detail-panel');
    const conversationId = detailPanel?.getAttribute('data-conversation-id');
    if (!conversationId || conversationId === '0') {
      alert('Please open a conversation to send images.');
      return;
    }

    const named = imageFiles.map(chatNamePastedImage);
    for (const file of named) {
      const { valid, error } = chatValidateFile(file, 'media');
      if (!valid) { alert(error); return; }
    }

    const attachments = chatBuildAttachmentsFromFiles(named, 'image');
    try {
      await sendMessage(null, null, attachments);
    } catch (err) {
      console.error('Error pasting image:', err);
      alert(err && err.message ? err.message : 'Failed to send. Please try again.');
    }
  });

});
